import logger from '../../lib/logger';
/**
 * ============================================
 * MAPPING MATTER-STYLE PROJECTION SYSTEM v3.0
 * ============================================
 * 
 * Supports 100+ projectors with SHADOW/OCCLUSION
 * 
 * Features:
 * - 256 projectors with no WebGL limits
 * - Single-sided projection (only surfaces facing projector)
 * - SHADOW/OCCLUSION via depth map comparison
 * - Multi-projector overlap with additive blending
 * - Test patterns, borders, custom images
 * - Lens shift, zoom, shutter control
 * - GPU memory estimation and warning system
 */

import * as BABYLON from '@babylonjs/core';
import { acquireVideo, releaseVideo, getVideoForProjector } from '../../lib/VideoTextureManager';
import { getProjectionOrigin } from './sceneUtils';

// Configuration
export const MAX_PROJECTORS = 256;
export const PROJECTION_TEXTURE_SIZE = 512;
export const SHADOW_ATLAS_SIZE = 4096;
export const SHADOW_TILE_SIZE = 384;
export const SHADOW_TILES_PER_ROW = 10;
export const MAX_SHADOW_PROJECTORS = 100; // Shadow atlas: 10x10 tiles of 384px each
const DATA_TEX_WIDTH = 2048;

// GPU Memory Configuration
const GPU_MEMORY_CONFIG = {
    // Estimated memory per projector (in MB)
    SHADOW_ATLAS_MB: (SHADOW_ATLAS_SIZE * SHADOW_ATLAS_SIZE * 2) / (1024 * 1024), // ~32MB for 4096x4096 R16F atlas (fixed cost)
    PROJECTION_TEXTURE_MB: (PROJECTION_TEXTURE_SIZE * PROJECTION_TEXTURE_SIZE * 4) / (1024 * 1024), // ~1MB for 512x512 RGBA
    BUFFER_OVERHEAD_MB: 0.5, // Additional buffers per projector
    
    // Default soft limits (can be adjusted by user)
    DEFAULT_SAFE_LIMIT: 32, // Safe for most GPUs (2GB+ VRAM)
    MIN_LIMIT: 8,
    MAX_LIMIT: 256,
    
    // Warning thresholds
    WARNING_THRESHOLD: 0.8, // Warn at 80% of limit
    CRITICAL_THRESHOLD: 0.95, // Critical at 95% of limit
    
    // Performance monitoring
    MIN_FPS_THRESHOLD: 30, // Warn if FPS drops below this
};

export const ASPECT_RATIOS = {
    '16:10': { value: 16/10, label: '16:10 (WUXGA)' },
    '16:9': { value: 16/9, label: '16:9 (HD)' },
    '4:3': { value: 4/3, label: '4:3 (XGA)' },
    '21:9': { value: 21/9, label: '21:9 (Ultrawide)' },
    '1:1': { value: 1, label: '1:1 (Square)' }
};

// Shadow depth vertex shader — passes world position for fragment-based depth computation
const SHADOW_DEPTH_VERTEX = `
precision highp float;
attribute vec3 position;
attribute vec3 normal;
uniform mat4 shadowViewProjection;
varying vec3 vWorldPos;

// Instance/thin instance support (also declares 'world' for non-instanced case)
#include<instancesDeclaration>

void main() {
    #include<instancesVertex>
    vec4 worldPos = finalWorld * vec4(position, 1.0);
    vWorldPos = worldPos.xyz;
    gl_Position = shadowViewProjection * worldPos;
}
`;

// Shadow depth fragment shader — computes depth from world position
// CRITICAL: this must match EXACTLY how the projection shader computes depth:
//   vec4 sc = vpMat * vec4(worldPos, 1.0);  fragDepth = sc.w;
const SHADOW_DEPTH_FRAGMENT = `
precision highp float;
uniform mat4 shadowViewProjection;
varying vec3 vWorldPos;

void main() {
    vec4 clipPos = shadowViewProjection * vec4(vWorldPos, 1.0);
    gl_FragColor = vec4(clipPos.w, 0.0, 0.0, 1.0);
}
`;

// Vertex shader
const PROJECTION_VERTEX_SHADER = `
precision highp float;
attribute vec3 position;
attribute vec3 normal;
attribute vec2 uv;
uniform mat4 worldViewProjection;
uniform mat4 world;
varying vec3 vWorldPosition;
varying vec3 vWorldNormal;
varying vec2 vUV;

void main() {
    vec4 worldPos = world * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;
    mat3 worldMat3 = mat3(world);
    vWorldNormal = normalize(worldMat3 * normal);
    vUV = uv;
    gl_Position = worldViewProjection * vec4(position, 1.0);
}
`;

// Simple mode fragment shader with SHADOW ATLAS (up to 7 projectors)
// WebGPU/WGSL compatible: ALL texture sampling in uniform control flow
// 7 proj textures + 1 shadow atlas + 1 object texture = 9 textures (well within 16 limit)
const PROJECTION_FRAGMENT_SHADER_SIMPLE = `
precision highp float;

varying vec3 vWorldPosition;
varying vec3 vWorldNormal;
varying vec2 vUV;

uniform vec3 baseColor;
uniform float ambientIntensity;
uniform int numProjectors;
uniform float materialAlpha;
uniform float debugShadowMode;
uniform float scrimAttenuation;
uniform float overlapBrightness;

uniform sampler2D objectDiffuseTexture;
uniform float hasObjectTexture;
uniform float objectUvMappingEnabled;
uniform float objectTextureBrightness;
uniform float objectTextureContrast;
uniform float objectTextureSaturation;

// Universal Texture Mapping (triplanar) — opt-in per receiver mesh.
// When useObjectTriplanar < 0.5 (default) behavior is identical to before.
// When >= 0.5 the object's diffuse texture is sampled by world-space
// triplanar projection instead of the mesh UV set, fixing dome/cylinder/
// broken-UV distortion. Does NOT change any projection blending math.
uniform float useObjectTriplanar;
uniform float objectTriplanarScale;

// Shadow atlas (single texture containing all projector depth maps)
uniform sampler2D shadowAtlas;

// Projector uniforms (7 max for simple mode)
uniform vec3 proj0Pos; uniform mat4 proj0VP; uniform float proj0Brightness; uniform float proj0Power; uniform sampler2D proj0Texture; uniform float proj0IsReverse; uniform float proj0Border;
uniform vec3 proj1Pos; uniform mat4 proj1VP; uniform float proj1Brightness; uniform float proj1Power; uniform sampler2D proj1Texture; uniform float proj1IsReverse; uniform float proj1Border;
uniform vec3 proj2Pos; uniform mat4 proj2VP; uniform float proj2Brightness; uniform float proj2Power; uniform sampler2D proj2Texture; uniform float proj2IsReverse; uniform float proj2Border;
uniform vec3 proj3Pos; uniform mat4 proj3VP; uniform float proj3Brightness; uniform float proj3Power; uniform sampler2D proj3Texture; uniform float proj3IsReverse; uniform float proj3Border;
uniform vec3 proj4Pos; uniform mat4 proj4VP; uniform float proj4Brightness; uniform float proj4Power; uniform sampler2D proj4Texture; uniform float proj4IsReverse; uniform float proj4Border;
uniform vec3 proj5Pos; uniform mat4 proj5VP; uniform float proj5Brightness; uniform float proj5Power; uniform sampler2D proj5Texture; uniform float proj5IsReverse; uniform float proj5Border;
uniform vec3 proj6Pos; uniform mat4 proj6VP; uniform float proj6Brightness; uniform float proj6Power; uniform sampler2D proj6Texture; uniform float proj6IsReverse; uniform float proj6Border;

vec3 computeProjectorUV(mat4 vpMatrix, vec3 worldPos) {
    vec4 clipPos = vpMatrix * vec4(worldPos, 1.0);
    vec3 ndc = clipPos.xyz / max(clipPos.w, 0.0001);
    vec2 uv = ndc.xy * 0.5 + 0.5;
    // Strict UV edge rejection — prevents projection leakage at borders
    float valid = step(0.001, clipPos.w)
                * step(0.01, uv.x) * step(uv.x, 0.99)
                * step(0.01, uv.y) * step(uv.y, 0.99);
    return vec3(uv, valid);
}

// Universal Texture Mapping helper — samples the object's diffuse texture
// using UV (default) or world-space triplanar projection (opt-in via the
// useObjectTriplanar uniform). Uniform control flow = safe on WebGPU.
// Cost when disabled: one branch test, zero extra texture reads.
vec4 sampleObjectAlbedo(vec2 meshUV) {
    if (useObjectTriplanar > 0.5) {
        vec3 triN = abs(normalize(vWorldNormal));
        triN = pow(triN, vec3(4.0));
        triN = triN / max(triN.x + triN.y + triN.z, 0.00001);
        vec4 triX = texture2D(objectDiffuseTexture, vWorldPosition.zy * objectTriplanarScale);
        vec4 triY = texture2D(objectDiffuseTexture, vWorldPosition.xz * objectTriplanarScale);
        vec4 triZ = texture2D(objectDiffuseTexture, vWorldPosition.xy * objectTriplanarScale);
        return triX * triN.x + triY * triN.y + triZ * triN.z;
    }
    return texture2D(objectDiffuseTexture, meshUV);
}

// Shadow atlas constants
const float SA_TILES_PER_ROW = 10.0;
const float SA_TILE_UV = 384.0 / 4096.0;
const float SA_TEXEL = 1.0 / 384.0;
const float SA_INSET = 2.0 / 384.0;
const float SA_CLEAR = 9000.0;

// Compute shadow from atlas - accesses global shadowAtlas (WebGPU safe: no sampler2D param)
float shadowFromAtlas(mat4 vpMat, vec3 wp, float idx) {
    vec4 sc = vpMat * vec4(wp, 1.0);

    // Behind-camera and max-distance gates (no early return — keeps texture sample uniform)
    float behindCamera = step(sc.w, 0.0);
    float tooFar = step(100.0, sc.w);

    // Safe perspective division
    float safeW = max(sc.w, 0.001);
    vec2 sNDC = sc.xy / safeW;

    // NDC → shadow UV  (standard NDC-to-UV, no Y-flip for RTT)
    vec2 sUV = vec2(sNDC.x * 0.5 + 0.5, sNDC.y * 0.5 + 0.5);

    // Frustum bounds check (UV in valid range)
    float inFrustum = step(0.005, sUV.x) * step(sUV.x, 0.995)
                    * step(0.005, sUV.y) * step(sUV.y, 0.995);

    // Map into the correct atlas tile for this projector
    vec2 tc = vec2(mod(idx, SA_TILES_PER_ROW), floor(idx / SA_TILES_PER_ROW));
    vec2 tOrigin = tc * SA_TILE_UV;
    vec2 cuv = clamp(sUV, SA_INSET, 1.0 - SA_INSET);
    vec2 auv = tOrigin + cuv * SA_TILE_UV;

    // Sample (always uniform flow — WebGPU safe)
    float sd = texture2D(shadowAtlas, auv).r;
    float fd = sc.w;

    // Ignore clear-value pixels (atlas cleared to 9999)
    float validDepth = step(sd, SA_CLEAR);

    // Ignore shadows from objects very close to projector origin (< 0.5m)
    // Prevents self-shadowing from own body parts and stacked projector bodies
    float farEnough = step(0.5, sd);

    // Adaptive bias — grows with distance for stability
    float bias = max(0.03, 0.003 * fd);

    // Shadow comparison — masked by frustum, valid depth, camera side, distance, near clip
    float inShadow = step(bias, fd - sd) * inFrustum * validDepth * farEnough
                   * (1.0 - behindCamera) * (1.0 - tooFar);

    return 1.0 - inShadow;

    return 1.0 - inShadow;
}

vec3 calcProjection(vec3 projPos, float brightness, float power, vec4 projColor, float shadowFactor, float uvValid, vec3 normal, vec3 worldPos, float isReverse, vec2 meshUV, vec2 projUV, float showBorder) {
    float projActive = step(0.5, power) * uvValid;
    vec3 toProjector = normalize(projPos - worldPos);
    // One-sided dot: a surface only receives projection when its normal
    // faces the projector. Using abs() here was leaking the image onto the
    // BACK faces of solid objects (e.g. the far face of a cube) because
    // user-imported 3D models aren't auto-registered as shadow occluders,
    // so the shadow term can't mask back faces. Domes / inverted meshes
    // intended for interior projection must be authored with INWARD-facing
    // normals (Babylon.Mesh.BACKSIDE), which keeps this dot positive there.
    float facingFactor = max(0.0, dot(normal, toProjector));
    
    // Surface facing check — reject surfaces nearly perpendicular to projector
    projActive *= step(0.05, facingFactor);
    
    // Shadow-based visibility — prevent projection leakage behind occluders
    projActive *= step(0.5, shadowFactor);
    
    // Border mask — white border at projection edges
    float borderSize = 0.02;
    float border = step(projUV.x, borderSize) + step(1.0 - borderSize, projUV.x)
                 + step(projUV.y, borderSize) + step(1.0 - borderSize, projUV.y);
    border = clamp(border, 0.0, 1.0);
    
    vec3 resultColor;
    if (isReverse > 0.5) {
        if (hasObjectTexture > 0.5) {
            vec4 objTexColor = sampleObjectAlbedo(meshUV);
            vec3 texRgb = objTexColor.rgb * objectTextureBrightness;
            texRgb = mix(vec3(0.5), texRgb, objectTextureContrast);
            float grey = dot(texRgb, vec3(0.2126, 0.7152, 0.0722));
            texRgb = mix(vec3(grey), texRgb, objectTextureSaturation);
            resultColor = texRgb * facingFactor * shadowFactor * projActive;
        } else {
            resultColor = baseColor * brightness * facingFactor * shadowFactor * projActive;
        }
    } else {
        resultColor = projColor.rgb * brightness * facingFactor * shadowFactor * projActive;
    }
    
    // Mix white border onto projection
    resultColor = mix(resultColor, vec3(1.0) * projActive, border * showBorder);
    
    return resultColor;
}

void main() {
    vec3 normal = normalize(vWorldNormal);
    if (!gl_FrontFacing) { normal = -normal; }
    
    vec3 baseDisplayColor = baseColor;
    if (hasObjectTexture > 0.5 && numProjectors < 1) {
        vec4 objTexColor = sampleObjectAlbedo(vUV);
        vec3 texRgb = objTexColor.rgb * objectTextureBrightness;
        texRgb = mix(vec3(0.5), texRgb, objectTextureContrast);
        float grey = dot(texRgb, vec3(0.2126, 0.7152, 0.0722));
        texRgb = mix(vec3(grey), texRgb, objectTextureSaturation);
        baseDisplayColor = texRgb;
    }
    
    vec3 finalColor = baseDisplayColor * ambientIntensity;
    
    vec3 uv0 = computeProjectorUV(proj0VP, vWorldPosition);
    vec3 uv1 = computeProjectorUV(proj1VP, vWorldPosition);
    vec3 uv2 = computeProjectorUV(proj2VP, vWorldPosition);
    vec3 uv3 = computeProjectorUV(proj3VP, vWorldPosition);
    vec3 uv4 = computeProjectorUV(proj4VP, vWorldPosition);
    vec3 uv5 = computeProjectorUV(proj5VP, vWorldPosition);
    vec3 uv6 = computeProjectorUV(proj6VP, vWorldPosition);

    vec4 pc0 = texture2D(proj0Texture, vec2(uv0.x, 1.0 - uv0.y));
    vec4 pc1 = texture2D(proj1Texture, vec2(uv1.x, 1.0 - uv1.y));
    vec4 pc2 = texture2D(proj2Texture, vec2(uv2.x, 1.0 - uv2.y));
    vec4 pc3 = texture2D(proj3Texture, vec2(uv3.x, 1.0 - uv3.y));
    vec4 pc4 = texture2D(proj4Texture, vec2(uv4.x, 1.0 - uv4.y));
    vec4 pc5 = texture2D(proj5Texture, vec2(uv5.x, 1.0 - uv5.y));
    vec4 pc6 = texture2D(proj6Texture, vec2(uv6.x, 1.0 - uv6.y));

    float sf0 = shadowFromAtlas(proj0VP, vWorldPosition, 0.0);
    float sf1 = shadowFromAtlas(proj1VP, vWorldPosition, 1.0);
    float sf2 = shadowFromAtlas(proj2VP, vWorldPosition, 2.0);
    float sf3 = shadowFromAtlas(proj3VP, vWorldPosition, 3.0);
    float sf4 = shadowFromAtlas(proj4VP, vWorldPosition, 4.0);
    float sf5 = shadowFromAtlas(proj5VP, vWorldPosition, 5.0);
    float sf6 = shadowFromAtlas(proj6VP, vWorldPosition, 6.0);

    // Accumulate projector contributions and count active projectors at this pixel
    vec3 totalProj = vec3(0.0);
    float projHitCount = 0.0;
    vec3 c;

    if (numProjectors > 0) { c = calcProjection(proj0Pos, proj0Brightness, proj0Power, pc0, sf0, uv0.z, normal, vWorldPosition, proj0IsReverse, vUV, uv0.xy, proj0Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }
    if (numProjectors > 1) { c = calcProjection(proj1Pos, proj1Brightness, proj1Power, pc1, sf1, uv1.z, normal, vWorldPosition, proj1IsReverse, vUV, uv1.xy, proj1Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }
    if (numProjectors > 2) { c = calcProjection(proj2Pos, proj2Brightness, proj2Power, pc2, sf2, uv2.z, normal, vWorldPosition, proj2IsReverse, vUV, uv2.xy, proj2Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }
    if (numProjectors > 3) { c = calcProjection(proj3Pos, proj3Brightness, proj3Power, pc3, sf3, uv3.z, normal, vWorldPosition, proj3IsReverse, vUV, uv3.xy, proj3Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }
    if (numProjectors > 4) { c = calcProjection(proj4Pos, proj4Brightness, proj4Power, pc4, sf4, uv4.z, normal, vWorldPosition, proj4IsReverse, vUV, uv4.xy, proj4Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }
    if (numProjectors > 5) { c = calcProjection(proj5Pos, proj5Brightness, proj5Power, pc5, sf5, uv5.z, normal, vWorldPosition, proj5IsReverse, vUV, uv5.xy, proj5Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }
    if (numProjectors > 6) { c = calcProjection(proj6Pos, proj6Brightness, proj6Power, pc6, sf6, uv6.z, normal, vWorldPosition, proj6IsReverse, vUV, uv6.xy, proj6Border) * (1.0 - scrimAttenuation); projHitCount += step(0.001, length(c)); totalProj += c; }

    // Overlap blend: 0 = seamless (averaged), 1 = full additive (current)
    if (projHitCount > 1.5) {
        vec3 avgProj = totalProj / projHitCount;
        finalColor += mix(avgProj, totalProj, overlapBrightness);
    } else {
        finalColor += totalProj;
    }

    if (debugShadowMode > 0.5 && numProjectors > 0) {
        vec4 dsc = proj0VP * vec4(vWorldPosition, 1.0);
        float dSafeW = max(dsc.w, 0.01);
        vec2 dsNDC = dsc.xy / dSafeW;
        vec2 dsUV = vec2(dsNDC.x * 0.5 + 0.5, dsNDC.y * 0.5 + 0.5);
        vec2 dtc = vec2(mod(0.0, SA_TILES_PER_ROW), floor(0.0 / SA_TILES_PER_ROW));
        vec2 dtOrigin = dtc * SA_TILE_UV;
        vec2 dcuv = clamp(dsUV, SA_INSET, 1.0 - SA_INSET);
        vec2 dauv = dtOrigin + dcuv * SA_TILE_UV;
        float dd = texture2D(shadowAtlas, dauv).r;
        float dFragDepth = dsc.w;
        
        if (debugShadowMode < 1.5) {
            // Mode 1: R=shadowDepth/50, G=fragDepth/50, B=shadowFactor
            finalColor = vec3(dd / 50.0, dFragDepth / 50.0, sf0);
        } else if (debugShadowMode < 2.5) {
            // Mode 2: Atlas UV coordinates for tile 0
            finalColor = vec3(dauv.x, dauv.y, 0.0);
        } else {
            // Mode 3: Depth comparison diagnostics
            float depthDiff = dFragDepth - dd;
            float bias = max(0.03, 0.003 * dFragDepth);
            float validD = step(dd, SA_CLEAR);
            finalColor = vec3(step(0.0, depthDiff) * validD, step(bias, depthDiff) * validD, validD);
        }
    }

    gl_FragColor = vec4(clamp(finalColor, 0.0, 1.0), materialAlpha);
}
`;

const PROJECTION_FRAGMENT_SHADER_COMPLEX = `
precision highp float;

varying vec3 vWorldPosition;
varying vec3 vWorldNormal;
varying vec2 vUV;

uniform vec3 baseColor;
uniform float ambientIntensity;
uniform int numProjectors;
uniform sampler2D projectorData;
uniform float dataTexWidth;
uniform sampler2D projectionAtlas;
uniform float materialAlpha;
uniform float scrimAttenuation;
uniform float overlapBrightness;

uniform sampler2D objectDiffuseTexture;
uniform float hasObjectTexture;
uniform float objectTextureBrightness;
uniform float objectTextureContrast;
uniform float objectTextureSaturation;

// Universal Texture Mapping — opt-in triplanar sampler (see SIMPLE shader
// for full notes). Uniform control flow = WebGPU-safe. Zero cost when disabled.
uniform float useObjectTriplanar;
uniform float objectTriplanarScale;

// Shadow atlas (single texture for all projector depth maps)
uniform sampler2D shadowAtlas;

// Shadow atlas constants
const float SA_TILES_PER_ROW = 10.0;
const float SA_TILE_UV = 384.0 / 4096.0;
const float SA_TEXEL = 1.0 / 384.0;
const float SA_INSET = 2.0 / 384.0;
const float SA_CLEAR = 9000.0;

void main() {
    vec3 normal = normalize(vWorldNormal);
    if (!gl_FrontFacing) { normal = -normal; }

    // Triplanar-aware object albedo sample (see SIMPLE shader for details).
    // Inlined here since the COMPLEX shader doesn't currently have a helper
    // function section — one branch on a uniform, zero cost when disabled.
    vec4 objTexColor;
    if (useObjectTriplanar > 0.5) {
        vec3 triN = abs(normalize(vWorldNormal));
        triN = pow(triN, vec3(4.0));
        triN = triN / max(triN.x + triN.y + triN.z, 0.00001);
        vec4 triX = texture2D(objectDiffuseTexture, vWorldPosition.zy * objectTriplanarScale);
        vec4 triY = texture2D(objectDiffuseTexture, vWorldPosition.xz * objectTriplanarScale);
        vec4 triZ = texture2D(objectDiffuseTexture, vWorldPosition.xy * objectTriplanarScale);
        objTexColor = triX * triN.x + triY * triN.y + triZ * triN.z;
    } else {
        objTexColor = texture2D(objectDiffuseTexture, vUV);
    }
    
    vec3 baseDisplayColor = baseColor;
    if (hasObjectTexture > 0.5 && numProjectors < 1) {
        vec3 texRgb = objTexColor.rgb * objectTextureBrightness;
        texRgb = mix(vec3(0.5), texRgb, objectTextureContrast);
        float grey = dot(texRgb, vec3(0.2126, 0.7152, 0.0722));
        texRgb = mix(vec3(grey), texRgb, objectTextureSaturation);
        baseDisplayColor = texRgb;
    }
    
    vec3 finalColor = baseDisplayColor * ambientIntensity;

    vec3 totalProj = vec3(0.0);
    float projHitCount = 0.0;

    for (int i = 0; i < 256; i++) {
        float loopActive = step(float(i) + 0.5, float(numProjectors));

        int basePixel = i * 8;
        float u0 = (float(basePixel) + 0.5) / dataTexWidth;
        float u1 = (float(basePixel + 1) + 0.5) / dataTexWidth;
        float u2 = (float(basePixel + 2) + 0.5) / dataTexWidth;
        float u3 = (float(basePixel + 3) + 0.5) / dataTexWidth;
        float u4 = (float(basePixel + 4) + 0.5) / dataTexWidth;
        float u5 = (float(basePixel + 5) + 0.5) / dataTexWidth;
        float u6 = (float(basePixel + 6) + 0.5) / dataTexWidth;
        float u7 = (float(basePixel + 7) + 0.5) / dataTexWidth;

        vec4 c0 = texture2D(projectorData, vec2(u0, 0.5));
        vec4 c1 = texture2D(projectorData, vec2(u1, 0.5));
        vec4 c2 = texture2D(projectorData, vec2(u2, 0.5));
        vec4 c3 = texture2D(projectorData, vec2(u3, 0.5));
        mat4 vpMatrix = mat4(c0, c1, c2, c3);

        vec4 posData = texture2D(projectorData, vec2(u4, 0.5));
        vec4 dirData = texture2D(projectorData, vec2(u5, 0.5));
        vec4 atlasData = texture2D(projectorData, vec2(u6, 0.5));
        vec4 extraData = texture2D(projectorData, vec2(u7, 0.5));

        vec3 projPos = posData.xyz;
        float brightness = posData.w;
        float power = dirData.w;
        float isReverse = extraData.r;

        // Projection UV
        vec4 clipPos = vpMatrix * vec4(vWorldPosition, 1.0);
        vec3 ndc = clipPos.xyz / max(clipPos.w, 0.0001);
        vec2 baseUV = clamp(ndc.xy * 0.5 + 0.5, 0.0, 1.0);
        vec2 projUV = vec2(baseUV.x, 1.0 - baseUV.y);

        // Shadow from atlas (reusing ndc from projection)
        float behindCam = step(clipPos.w, 0.0);
        float tooFarSh = step(100.0, clipPos.w);
        vec2 sUV = vec2(ndc.x * 0.5 + 0.5, ndc.y * 0.5 + 0.5);
        float inBounds = step(0.005, sUV.x) * step(sUV.x, 0.995)
                       * step(0.005, sUV.y) * step(sUV.y, 0.995);
        float projIdx = float(i);
        vec2 tileCoord = vec2(mod(projIdx, SA_TILES_PER_ROW), floor(projIdx / SA_TILES_PER_ROW));
        vec2 tileOrigin = tileCoord * SA_TILE_UV;
        vec2 clampedSUV = clamp(sUV, SA_INSET, 1.0 - SA_INSET);
        vec2 shadowAtlasUV = tileOrigin + clampedSUV * SA_TILE_UV;
        float shadowDepth = texture2D(shadowAtlas, shadowAtlasUV).r;
        float fragDepth = clipPos.w;
        float validDepth = step(shadowDepth, SA_CLEAR);
        float farEnough = step(0.5, shadowDepth);
        float bias = max(0.03, 0.003 * fragDepth);
        float hasShadow = step(float(i) + 0.5, 100.0);
        float inShadow = step(bias, fragDepth - shadowDepth) * inBounds * hasShadow * validDepth * farEnough
                       * (1.0 - behindCam) * (1.0 - tooFarSh);
        float shadow = 1.0 - inShadow;

        // Projection atlas color
        vec2 atlasOffset = atlasData.xy;
        vec2 atlasScale = atlasData.zw;
        vec2 projAtlasUV = atlasOffset + projUV * atlasScale;
        vec4 projColor = texture2D(projectionAtlas, projAtlasUV);

        // Compute contribution
        float projActive = step(0.5, power) * loopActive;
        vec3 toProjector = normalize(projPos - vWorldPosition + vec3(0.0001));
        // One-sided facing — see calcProjection() above for the full reason.
        float facingFactor = max(0.0, dot(normal, toProjector));
        projActive *= step(0.001, clipPos.w);
        // Strict UV edge rejection — prevents projection leakage at borders
        projActive *= step(0.01, baseUV.x) * step(baseUV.x, 0.99);
        projActive *= step(0.01, baseUV.y) * step(baseUV.y, 0.99);
        // Surface facing check — reject surfaces nearly perpendicular to projector
        projActive *= step(0.05, facingFactor);
        // Shadow-based visibility — prevent projection leakage behind occluders
        projActive *= step(0.5, shadow);

        // Color based on reverse mode
        vec3 contribution;
        if (isReverse > 0.5 && hasObjectTexture > 0.5) {
            vec3 texRgb = objTexColor.rgb * objectTextureBrightness;
            texRgb = mix(vec3(0.5), texRgb, objectTextureContrast);
            float grey = dot(texRgb, vec3(0.2126, 0.7152, 0.0722));
            texRgb = mix(vec3(grey), texRgb, objectTextureSaturation);
            contribution = texRgb * facingFactor * shadow * projActive;
        } else if (isReverse > 0.5) {
            contribution = baseColor * brightness * facingFactor * shadow * projActive;
        } else {
            contribution = projColor.rgb * brightness * facingFactor * shadow * projActive;
        }
        
        // Border mask — white border at projection edges
        float borderSize = 0.02;
        float borderMask = step(projUV.x, borderSize) + step(1.0 - borderSize, projUV.x)
                         + step(projUV.y, borderSize) + step(1.0 - borderSize, projUV.y);
        borderMask = clamp(borderMask, 0.0, 1.0);
        float showBorder = extraData.g; // show_border flag from data texture
        contribution = mix(contribution, vec3(1.0) * projActive, borderMask * showBorder);
        
        vec3 finalContrib = contribution * (1.0 - scrimAttenuation);
        projHitCount += step(0.001, length(finalContrib));
        totalProj += finalContrib;
    }

    // Overlap blend: 0 = seamless (averaged), 1 = full additive
    if (projHitCount > 1.5) {
        vec3 avgProj = totalProj / projHitCount;
        finalColor += mix(avgProj, totalProj, overlapBrightness);
    } else {
        finalColor += totalProj;
    }

    gl_FragColor = vec4(clamp(finalColor, 0.0, 1.0), materialAlpha);
}
`;

export class ProjectionSystem {
    constructor(scene) {
        this.scene = scene;
        this.projectors = [];
        this.receiverMeshes = new Map();
        this.occluderMeshes = new Map(); // Objects that can cast shadows
        this._projectorOccluderIds = new Map(); // Maps occluder meshId → owner projector ID (for self-shadow exclusion)
        
        // Textures
        this.projectorTextures = new Map();
        this.defaultTextures = [];
        
        // Data textures for complex mode
        this.projectorDataTexture = null;
        this.projectorDataArray = null;
        
        // Atlas
        this.projectionAtlas = null;
        this.atlasSize = 4096;
        this.patternSize = 256;
        this.atlasColumns = 16;
        this.atlasRows = 16;
        
        // Shadow atlas (single RTT for all projector depth maps)
        this.shadowAtlasRTT = null;
        this.depthMaterial = null;
        this._vpMatrices = [];
        this._dirtyTiles = new Set();
        this._projectorFingerprints = [];
        this._occluderVersion = 0;
        this._lastOccluderVersion = -1;
        this._shadowNeedsUpdate = true;
        this._atlasRenderedOnce = false;
        this._shadowRenderLogged = false;
        
        // Mode
        this.mode = 'simple';
        this.maxSimpleProjectors = 7; // WebGPU limit: 7 proj textures + 1 shadow atlas + 1 object = 9 textures
        
        // GPU Memory Management
        this.gpuMemory = {
            softLimit: GPU_MEMORY_CONFIG.DEFAULT_SAFE_LIMIT,
            estimatedUsageMB: 0,
            lastFpsCheck: 0,
            fpsWarningShown: false,
            memoryWarningCallback: null, // Callback for UI warnings
        };
        
        // Custom image cache - PERSISTENT across React renders
        // Maps projectorId -> { img: HTMLImageElement, imageUrl: string }
        this._customImageCache = new Map();

        // Custom video cache — PERSISTENT, keyed by projector id.
        // Maps projectorId -> { videoUrl: string }. The actual HTMLVideoElement
        // is owned by VideoTextureManager (shared across projectors that use
        // the same source for "global sync" playback).
        this._customVideoCache = new Map();
        // Per-frame tick to flag whether at least one projector needs a video
        // blit this frame. Registered lazily when the first video source is set.
        this._videoTickObserver = null;

        // Sticky shader uniforms — applied to NEW materials created by
        // registerReceiver() (createSimpleMaterial / createComplexMaterial).
        // Without this, when the user reloads a project, setOverlapBrightness()
        // is called BEFORE any receiver meshes have been registered (async
        // model load), so the for-loop is a no-op and the saved value is lost.
        // Storing it here ensures every newly-registered material picks up the
        // current value at creation time.
        this._overlapBrightness = 1.0;
        
        // Initialize
        this._registerShaders();
        this._initDataTextures();
        this._initDefaultTextures();
        this._initShadowSystem();
        
        logger.log('ProjectionSystem v3.0: Initialized with SHADOW/OCCLUSION support');
    }
    
    // ============================================
    // GPU MEMORY MANAGEMENT
    // ============================================
    
    /**
     * Set callback for memory warnings (to show in UI)
     * @param {Function} callback - Function to call with warning message
     */
    setMemoryWarningCallback(callback) {
        this.gpuMemory.memoryWarningCallback = callback;
    }
    
    /**
     * Set the soft limit for projectors
     * @param {number} limit - Maximum number of projectors
     */
    setProjectorLimit(limit) {
        this.gpuMemory.softLimit = Math.max(
            GPU_MEMORY_CONFIG.MIN_LIMIT,
            Math.min(GPU_MEMORY_CONFIG.MAX_LIMIT, limit)
        );
        logger.log(`ProjectionSystem: Projector limit set to ${this.gpuMemory.softLimit}`);
    }
    
    /**
     * Get the current projector limit
     * @returns {number}
     */
    getProjectorLimit() {
        return this.gpuMemory.softLimit;
    }
    
    /**
     * Estimate memory usage for a given number of projectors
     * @param {number} projectorCount 
     * @returns {number} Estimated memory in MB
     */
    estimateMemoryUsage(projectorCount) {
        const shadowCount = Math.min(projectorCount, MAX_SHADOW_PROJECTORS);
        
        // Calculate memory usage
        const shadowMapMemory = GPU_MEMORY_CONFIG.SHADOW_ATLAS_MB; // Fixed cost: single shadow atlas
        const projectionTextureMemory = projectorCount * GPU_MEMORY_CONFIG.PROJECTION_TEXTURE_MB;
        const bufferMemory = projectorCount * GPU_MEMORY_CONFIG.BUFFER_OVERHEAD_MB;
        
        // Atlas memory (fixed)
        const atlasMemory = (this.atlasSize * this.atlasSize * 4) / (1024 * 1024); // ~64MB for 4096x4096
        
        // Data texture memory (fixed)
        const dataTextureMemory = (DATA_TEX_WIDTH * 4 * 4) / (1024 * 1024); // Small
        
        return shadowMapMemory + projectionTextureMemory + bufferMemory + atlasMemory + dataTextureMemory;
    }
    
    /**
     * Check if a new projector can be safely added
     * @returns {{ canAdd: boolean, reason: string, currentCount: number, limit: number, memoryMB: number }}
     */
    canAddProjector() {
        const currentCount = this.projectors.length;
        const limit = this.gpuMemory.softLimit;
        const memoryMB = this.estimateMemoryUsage(currentCount + 1);
        
        if (currentCount >= limit) {
            return {
                canAdd: false,
                reason: `Projector limit reached (${limit}). Increase limit in settings or remove projectors.`,
                currentCount,
                limit,
                memoryMB
            };
        }
        
        if (currentCount >= MAX_PROJECTORS) {
            return {
                canAdd: false,
                reason: `Maximum projector limit reached (${MAX_PROJECTORS}).`,
                currentCount,
                limit: MAX_PROJECTORS,
                memoryMB
            };
        }
        
        // Check warning thresholds
        const usageRatio = (currentCount + 1) / limit;
        let warning = null;
        
        if (usageRatio >= GPU_MEMORY_CONFIG.CRITICAL_THRESHOLD) {
            warning = `Critical: ${currentCount + 1}/${limit} projectors. Performance may degrade.`;
        } else if (usageRatio >= GPU_MEMORY_CONFIG.WARNING_THRESHOLD) {
            warning = `Warning: Approaching projector limit (${currentCount + 1}/${limit}).`;
        }
        
        return {
            canAdd: true,
            reason: warning,
            currentCount,
            limit,
            memoryMB
        };
    }
    
    /**
     * Get GPU memory status for display
     * @returns {{ currentProjectors: number, limit: number, usagePercent: number, estimatedMemoryMB: number, status: 'ok' | 'warning' | 'critical' }}
     */
    getMemoryStatus() {
        const currentCount = this.projectors.length;
        const limit = this.gpuMemory.softLimit;
        const usagePercent = (currentCount / limit) * 100;
        const estimatedMemoryMB = this.estimateMemoryUsage(currentCount);
        
        let status = 'ok';
        if (usagePercent >= GPU_MEMORY_CONFIG.CRITICAL_THRESHOLD * 100) {
            status = 'critical';
        } else if (usagePercent >= GPU_MEMORY_CONFIG.WARNING_THRESHOLD * 100) {
            status = 'warning';
        }
        
        return {
            currentProjectors: currentCount,
            limit,
            usagePercent: Math.round(usagePercent),
            estimatedMemoryMB: Math.round(estimatedMemoryMB * 10) / 10,
            status
        };
    }
    
    /**
     * Show memory warning via callback
     * @param {string} message 
     * @param {'info' | 'warning' | 'error'} type 
     */
    _showMemoryWarning(message, type = 'warning') {
        logger.warn(`ProjectionSystem: ${message}`);
        if (this.gpuMemory.memoryWarningCallback) {
            this.gpuMemory.memoryWarningCallback(message, type);
        }
    }
    
    _registerShaders() {
        BABYLON.Effect.ShadersStore['projComplexVertexShader'] = PROJECTION_VERTEX_SHADER;
        BABYLON.Effect.ShadersStore['projComplexFragmentShader'] = PROJECTION_FRAGMENT_SHADER_COMPLEX;
        BABYLON.Effect.ShadersStore['projSimpleVertexShader'] = PROJECTION_VERTEX_SHADER;
        BABYLON.Effect.ShadersStore['projSimpleFragmentShader'] = PROJECTION_FRAGMENT_SHADER_SIMPLE;
        // Depth shaders are registered in _initShadowSystem()
    }
    
    _initDataTextures() {
        this.projectorDataArray = new Float32Array(DATA_TEX_WIDTH * 4);
        
        this.projectorDataTexture = new BABYLON.RawTexture(
            this.projectorDataArray,
            DATA_TEX_WIDTH,
            1,
            BABYLON.Engine.TEXTUREFORMAT_RGBA,
            this.scene,
            false,
            false,
            BABYLON.Texture.NEAREST_SAMPLINGMODE,
            BABYLON.Engine.TEXTURETYPE_FLOAT
        );
        this.projectorDataTexture.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
        this.projectorDataTexture.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
        
        this.projectionAtlas = new BABYLON.DynamicTexture(
            'projectionAtlas',
            { width: this.atlasSize, height: this.atlasSize },
            this.scene,
            false,
            BABYLON.Texture.NEAREST_SAMPLINGMODE
        );
        this.projectionAtlas.hasAlpha = false;
        // Hidden canvas for drawing patterns (used to extract pixel data for WebGPU-safe RawTexture)
        this._atlasCanvas = document.createElement('canvas');
        this._atlasCanvas.width = this.atlasSize;
        this._atlasCanvas.height = this.atlasSize;
        this._atlasCtx = this._atlasCanvas.getContext('2d');
        // RawTexture atlas for WebGPU compatibility (DynamicTexture.update() unreliable on WebGPU)
        const initData = new Uint8Array(this.atlasSize * this.atlasSize * 4);
        initData.fill(255); // white
        this.atlasRawTexture = new BABYLON.RawTexture(
            initData,
            this.atlasSize, this.atlasSize,
            BABYLON.Engine.TEXTUREFORMAT_RGBA,
            this.scene,
            false, false,
            BABYLON.Texture.NEAREST_SAMPLINGMODE,
            BABYLON.Engine.TEXTURETYPE_UNSIGNED_BYTE
        );
        this.atlasRawTexture.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
        this.atlasRawTexture.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
        
        this._initializeAtlas();
    }
    
    _initializeAtlas() {
        const ctx = this._atlasCtx;
        for (let row = 0; row < this.atlasRows; row++) {
            for (let col = 0; col < this.atlasColumns; col++) {
                const x = col * this.patternSize;
                const y = row * this.patternSize;
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(x, y, this.patternSize, this.patternSize);
                this._drawGridToContext(ctx, x, y, this.patternSize);
            }
        }
        this._uploadAtlasToRawTexture();
    }
    
    _uploadAtlasToRawTexture() {
        const imageData = this._atlasCtx.getImageData(0, 0, this.atlasSize, this.atlasSize);
        this.atlasRawTexture.update(imageData.data);
    }
    
    _initDefaultTextures() {
        for (let i = 0; i < this.maxSimpleProjectors; i++) {
            const texture = new BABYLON.DynamicTexture(
                `defaultProjTex_${i}`,
                { width: PROJECTION_TEXTURE_SIZE, height: PROJECTION_TEXTURE_SIZE },
                this.scene,
                true
            );
            texture.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
            texture.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
            
            const ctx = texture.getContext();
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, PROJECTION_TEXTURE_SIZE, PROJECTION_TEXTURE_SIZE);
            this._drawGridToContext(ctx, 0, 0, PROJECTION_TEXTURE_SIZE);
            texture.update();
            
            this.defaultTextures.push(texture);
        }
    }
    
    _initShadowSystem() {
        // Register depth shaders
        BABYLON.Effect.ShadersStore['shadowDepthVertexShader'] = SHADOW_DEPTH_VERTEX;
        BABYLON.Effect.ShadersStore['shadowDepthFragmentShader'] = SHADOW_DEPTH_FRAGMENT;
        
        // Create shared depth material for shadow map rendering
        this.depthMaterial = new BABYLON.ShaderMaterial('shadowDepth', this.scene, {
            vertex: 'shadowDepth',
            fragment: 'shadowDepth'
        }, {
            attributes: ['position', 'normal'],
            uniforms: ['world', 'shadowViewProjection'],
            uniformBuffers: ['Scene', 'Mesh']
        });
        this.depthMaterial.backFaceCulling = false;
        
        // Per-projector depth material pool — each projector gets its OWN material
        // instance to guarantee isolated uniform buffers in WebGPU.
        // Sharing one ShaderMaterial and changing uniforms per tile fails because
        // WebGPU caches bind groups and does not recreate them mid-render-pass.
        this.depthMaterials = [];
        for (let i = 0; i < MAX_SHADOW_PROJECTORS; i++) {
            const mat = new BABYLON.ShaderMaterial('shadowDepth_' + i, this.scene, {
                vertex: 'shadowDepth',
                fragment: 'shadowDepth'
            }, {
                attributes: ['position', 'normal'],
                uniforms: ['world', 'shadowViewProjection'],
                uniformBuffers: ['Scene', 'Mesh']
            });
            mat.backFaceCulling = false;
            this.depthMaterials.push(mat);
        }
        
        // VP matrices for each projector (up to 100)
        this._vpMatrices = new Array(MAX_SHADOW_PROJECTORS).fill(null).map(() => BABYLON.Matrix.Identity());
        
        // Dirty tile tracking
        this._dirtyTiles = new Set();
        this._projectorFingerprints = new Array(MAX_SHADOW_PROJECTORS).fill('');
        this._occluderVersion = 0;
        this._lastOccluderVersion = -1;
        this._shadowNeedsUpdate = true;
        
        // Create shadow atlas RTT (single texture for ALL projector depth maps)
        this.shadowAtlasRTT = new BABYLON.RenderTargetTexture(
            'shadowAtlas',
            { width: SHADOW_ATLAS_SIZE, height: SHADOW_ATLAS_SIZE },
            this.scene,
            false,  // generateMipMaps
            true,   // doNotChangeAspectRatio
            BABYLON.Constants.TEXTURETYPE_FLOAT, // Full float for depth precision
            false,  // isCube
            BABYLON.Texture.NEAREST_SAMPLINGMODE,
            true,   // generateDepthBuffer
            false   // generateStencilBuffer
        );
        this.shadowAtlasRTT.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
        this.shadowAtlasRTT.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
        this.shadowAtlasRTT.clearColor = new BABYLON.Color4(9999, 0, 0, 1);
        this.shadowAtlasRTT.refreshRate = BABYLON.RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYFRAME;
        this.shadowAtlasRTT.renderParticles = false;
        this.shadowAtlasRTT.renderSprites = false;
        this.shadowAtlasRTT.renderList = [];
        
        // Custom render function: renders each projector's depth into its atlas tile
        const self = this;
        this.shadowAtlasRTT.customRenderFunction = function(opaqueSubMeshes, alphaTestSubMeshes, transparentSubMeshes, depthOnlySubMeshes) {
            const engine = self.scene.getEngine();
            const numProj = Math.min(self.projectors.length, MAX_SHADOW_PROJECTORS);
            
            if (numProj === 0 || opaqueSubMeshes.length === 0) return;
            
            const tileUV = SHADOW_TILE_SIZE / SHADOW_ATLAS_SIZE;
            const clearColor = self.shadowAtlasRTT.clearColor;
            
            // Collect unique meshes for material save/restore (global swap)
            const savedMaterials = new Map();
            for (let j = 0; j < opaqueSubMeshes.length; j++) {
                const mesh = opaqueSubMeshes.data[j].getRenderingMesh();
                if (!savedMaterials.has(mesh)) {
                    savedMaterials.set(mesh, mesh.material);
                }
            }
            
            for (let i = 0; i < numProj; i++) {
                const col = i % SHADOW_TILES_PER_ROW;
                const row = Math.floor(i / SHADOW_TILES_PER_ROW);
                
                // Set viewport and scissor to this tile region
                engine.setViewport(new BABYLON.Viewport(col * tileUV, row * tileUV, tileUV, tileUV));
                engine.enableScissor(col * SHADOW_TILE_SIZE, row * SHADOW_TILE_SIZE, SHADOW_TILE_SIZE, SHADOW_TILE_SIZE);
                
                // Clear BOTH color AND depth for this tile
                engine.clear(clearColor, true, true);
                
                // Use this projector's DEDICATED depth material (separate uniform buffer)
                const tileMat = self.depthMaterials[i];
                
                // Find this projector's own body mesh IDs to exclude from self-shadowing
                const projId = self.projectors[i]?.id;
                const selfMeshIds = new Set();
                if (projId && self._projectorOccluderIds) {
                    self._projectorOccluderIds.forEach((ownerId, meshId) => {
                        if (ownerId === projId) selfMeshIds.add(meshId);
                    });
                }
                
                // Swap ALL mesh materials to this projector's depth material (global swap)
                savedMaterials.forEach((_, mesh) => {
                    mesh.material = tileMat;
                });
                
                // Render only non-self submeshes (skip own projector body)
                for (let j = 0; j < opaqueSubMeshes.length; j++) {
                    const subMesh = opaqueSubMeshes.data[j];
                    const renderMesh = subMesh.getRenderingMesh();
                    const mid = renderMesh.uniqueId || renderMesh.name;
                    if (selfMeshIds.has(mid)) continue;
                    subMesh.render(false);
                }
                
                engine.disableScissor();
            }
            
            // Restore ALL original materials (single global restore)
            savedMaterials.forEach((mat, mesh) => {
                mesh.material = mat;
            });
            
            // Reset viewport to full atlas size
            engine.setViewport(new BABYLON.Viewport(0, 0, 1, 1));
            
            // Mark atlas as rendered
            self._atlasRenderedOnce = true;
            
            if (!self._shadowRenderLogged) {
                self._shadowRenderLogged = true;
                logger.log('[Shadow Atlas] Rendered: ' + numProj + ' projectors, ' + opaqueSubMeshes.length + ' submeshes (per-projector material pool)');
            }
        };
        
        this.scene.customRenderTargets.push(this.shadowAtlasRTT);
        
        logger.log('ProjectionSystem: Shadow atlas initialized (' + SHADOW_ATLAS_SIZE + 'x' + SHADOW_ATLAS_SIZE + ', ' + SHADOW_TILES_PER_ROW + 'x' + SHADOW_TILES_PER_ROW + ' tiles of ' + SHADOW_TILE_SIZE + 'px)');
    }
    
    
        _drawGridToContext(ctx, x, y, size) {
        // Black background - blocks light, creating visual "transparency" in projection
        // (In spotlight projection, black = no light = transparent effect)
        ctx.fillStyle = '#000000';
        ctx.fillRect(x, y, size, size);
        
        // Thin green grid lines - professional calibration look
        ctx.strokeStyle = '#00ff00';
        ctx.lineWidth = 1;
        
        // 16 vertical lines
        const verticalLines = 16;
        for (let i = 0; i <= verticalLines; i++) {
            const offset = (size * i) / verticalLines;
            ctx.beginPath();
            ctx.moveTo(x + offset, y);
            ctx.lineTo(x + offset, y + size);
            ctx.stroke();
        }
        
        // 10 horizontal lines
        const horizontalLines = 10;
        for (let i = 0; i <= horizontalLines; i++) {
            const offset = (size * i) / horizontalLines;
            ctx.beginPath();
            ctx.moveTo(x, y + offset);
            ctx.lineTo(x + size, y + offset);
            ctx.stroke();
        }
    }
    
    _drawTestPattern(ctx, x, y, size, patternId) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x, y, size, size);
        
        switch (patternId) {
            case 'grid':
                this._drawGridToContext(ctx, x, y, size);
                break;
            case 'color_bars':
                const colors = ['#ffffff', '#ffff00', '#00ffff', '#00ff00', '#ff00ff', '#ff0000', '#0000ff', '#000000'];
                const barW = size / colors.length;
                colors.forEach((color, i) => {
                    ctx.fillStyle = color;
                    ctx.fillRect(x + i * barW, y, barW + 1, size);
                });
                break;
            case 'checkerboard':
                const checkSize = 8;
                const cellSize = size / checkSize;
                for (let i = 0; i < checkSize; i++) {
                    for (let j = 0; j < checkSize; j++) {
                        ctx.fillStyle = (i + j) % 2 === 0 ? '#ffffff' : '#000000';
                        ctx.fillRect(x + i * cellSize, y + j * cellSize, cellSize + 1, cellSize + 1);
                    }
                }
                break;
            case 'rgbw':
                const half = size / 2;
                ctx.fillStyle = '#ff0000';
                ctx.fillRect(x, y, half, half);
                ctx.fillStyle = '#00ff00';
                ctx.fillRect(x + half, y, half, half);
                ctx.fillStyle = '#0000ff';
                ctx.fillRect(x, y + half, half, half);
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(x + half, y + half, half, half);
                break;
            case 'focus':
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 1;
                const lines = 16;
                for (let i = 0; i <= lines; i++) {
                    const off = (size * i) / lines;
                    ctx.beginPath();
                    ctx.moveTo(x + off, y);
                    ctx.lineTo(x + off, y + size);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(x, y + off);
                    ctx.lineTo(x + size, y + off);
                    ctx.stroke();
                }
                ctx.strokeStyle = '#ff0000';
                ctx.lineWidth = 2;
                const cx = x + size / 2;
                const cy = y + size / 2;
                for (let r = size / 8; r < size / 2; r += size / 8) {
                    ctx.beginPath();
                    ctx.arc(cx, cy, r, 0, Math.PI * 2);
                    ctx.stroke();
                }
                break;
            case 'white':
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(x, y, size, size);
                break;
            case 'red':
                ctx.fillStyle = '#ff0000';
                ctx.fillRect(x, y, size, size);
                break;
            case 'green':
                ctx.fillStyle = '#00ff00';
                ctx.fillRect(x, y, size, size);
                break;
            case 'blue':
                ctx.fillStyle = '#0000ff';
                ctx.fillRect(x, y, size, size);
                break;
            case 'cyan':
                ctx.fillStyle = '#00ffff';
                ctx.fillRect(x, y, size, size);
                break;
            case 'magenta':
                ctx.fillStyle = '#ff00ff';
                ctx.fillRect(x, y, size, size);
                break;
            case 'yellow':
                ctx.fillStyle = '#ffff00';
                ctx.fillRect(x, y, size, size);
                break;
            case 'black':
                ctx.fillStyle = '#000000';
                ctx.fillRect(x, y, size, size);
                break;
            case 'gradient':
            case 'gradient-h':
                const gradientH = ctx.createLinearGradient(x, y, x + size, y);
                gradientH.addColorStop(0, '#000000');
                gradientH.addColorStop(1, '#ffffff');
                ctx.fillStyle = gradientH;
                ctx.fillRect(x, y, size, size);
                break;
            case 'gradient-v':
                const gradientV = ctx.createLinearGradient(x, y, x, y + size);
                gradientV.addColorStop(0, '#000000');
                gradientV.addColorStop(1, '#ffffff');
                ctx.fillStyle = gradientV;
                ctx.fillRect(x, y, size, size);
                break;
            case 'crosshatch':
                ctx.strokeStyle = '#000000';
                ctx.lineWidth = 2;
                for (let i = 0; i < size; i += size / 10) {
                    ctx.beginPath();
                    ctx.moveTo(x + i, y);
                    ctx.lineTo(x + size, y + size - i);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(x, y + i);
                    ctx.lineTo(x + size - i, y + size);
                    ctx.stroke();
                }
                break;
            case 'border':
                // Just border - solid white with thick border
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(x, y, size, size);
                ctx.strokeStyle = '#ff00ff';
                ctx.lineWidth = Math.max(8, size / 20);
                ctx.strokeRect(x + ctx.lineWidth / 2, y + ctx.lineWidth / 2, size - ctx.lineWidth, size - ctx.lineWidth);
                break;
            default:
                this._drawGridToContext(ctx, x, y, size);
        }
    }
    
    _calculateProjectorVPMatrix(projector) {
        // Get projection origin — for lens90, this is the lens exit point
        const basePos = new BABYLON.Vector3(
            projector.position?.x || 0,
            projector.position?.y || 0,
            projector.position?.z || 0
        );
        
        const rotX = (projector.rotation?.x || 0) * Math.PI / 180;
        const rotY = (projector.rotation?.y || 0) * Math.PI / 180;
        const rotZ = (projector.rotation?.z || 0) * Math.PI / 180;
        
        const rotationMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
        
        let position;
        if (projector.lensType === 'lens90') {
            // lens90 apex (incl. +100mm X, +20mm Z body-local nudge) baked
            // into getProjectionOrigin — single source of truth shared with
            // the beam wireframe and the cyan surface overlay.
            position = getProjectionOrigin(projector, {});
        } else if (projector.lensType === 'topBottom') {
            // Periscope apex baked into getProjectionOrigin — single source.
            position = getProjectionOrigin(projector, {});
        } else if (projector.lensType === 'ust') {
            // UST projection-matrix apex = mirror mesh world position +
            // body-local ustNudge.
            //
            // Z = 0.10 (260mm SMALLER than the beam-wireframe's 0.36) shrinks
            // the rendered image to match the calculator's expected
            // throw_distance/throw_ratio (e.g. 3000mm at 3m / 1.0).
            //
            // Y = 0.03 (80mm LOWER than the beam apex's 0.11) shifts the
            // rendered image down by exactly 80mm at the wall so it lines up
            // perfectly inside the beam wireframe. The visual beam in
            // BabylonScene.js keeps its own (0, 0.11, 0.36) nudge — only the
            // projection texture moves, never the beam.
            let apex = null;
            if (this.scene && this.scene.getMeshByName) {
                const mirrorMesh = this.scene.getMeshByName('ustGlassv4_' + projector.id);
                if (mirrorMesh) {
                    mirrorMesh.computeWorldMatrix(true);
                    apex = mirrorMesh.getAbsolutePosition().clone();
                }
            }
            if (!apex) {
                apex = getProjectionOrigin(projector, {});
            }
            const ustNudgeLocal = new BABYLON.Vector3(0, 0.03, 0.10);
            const ustNudgeWorld = BABYLON.Vector3.TransformNormal(ustNudgeLocal, rotationMatrix);
            position = apex.add(ustNudgeWorld);
        } else {
            // Standard lens: offset from body center to lens front face + small forward push
            // The extra 0.005m (5mm) prevents the projector's own near-wall geometry
            // from appearing in the shadow depth map and self-occluding the projection.
            const bodyD = (projector.body_depth || projector.spec?.depth || 600) / 1000;
            const forward = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, 1), rotationMatrix).normalize();
            position = basePos.add(forward.scale(bodyD / 2 + 0.005));
        }
        
        let direction;
        let up;
        
        if (projector.lensType === 'lens90') {
            // 90-degree lens: base direction in projector-local space, then apply rotation
            const rot = projector.lens90Rotation || 'right';
            let localDir, localUp;
            switch (rot) {
                case 'left':   localDir = new BABYLON.Vector3(-1, 0, 0); break;
                case 'right':  localDir = new BABYLON.Vector3( 1, 0, 0); break;
                case 'top':    localDir = new BABYLON.Vector3( 0, 1, 0); break;
                case 'bottom': localDir = new BABYLON.Vector3( 0,-1, 0); break;
                default:       localDir = new BABYLON.Vector3( 1, 0, 0); break;
            }
            if (rot === 'top') localUp = new BABYLON.Vector3(0, 0, 1);
            else if (rot === 'bottom') localUp = new BABYLON.Vector3(0, 0, -1);
            else localUp = new BABYLON.Vector3(0, 1, 0);
            // Transform by projector rotation to get world direction
            direction = BABYLON.Vector3.TransformNormal(localDir, rotationMatrix).normalize();
            up = BABYLON.Vector3.TransformNormal(localUp, rotationMatrix).normalize();
        } else if (projector.lensType === 'topBottom') {
            // Periscope folds back; beam exits along -Z local. Vertical bias
            // for projected image is applied via lensShiftV (below) — no
            // direction tilt here, keeps it consistent with beam wireframe.
            const lensShiftH = (projector.lens_shift_h || 0) / 100;
            const baseDir = new BABYLON.Vector3(-lensShiftH * 0.5, 0, -1).normalize();
            direction = BABYLON.Vector3.TransformNormal(baseDir, rotationMatrix).normalize();
            up = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 1, 0), rotationMatrix);
        } else if (projector.lensType === 'ust') {
            // UST: lens at FRONT, beam fires STRAIGHT BACK (-Z) horizontally.
            // Vertical UP bias is applied via lensShiftV alone (below) — do
            // NOT tilt the direction here, otherwise the projection ends up
            // shifted twice and lands higher than the beam wireframe shows.
            const lensShiftH = (projector.lens_shift_h || 0) / 100;
            const baseDir = new BABYLON.Vector3(
                lensShiftH * 0.5,
                0,
                -1
            ).normalize();
            const baseUp = new BABYLON.Vector3(0, 1, 0);
            direction = BABYLON.Vector3.TransformNormal(baseDir, rotationMatrix).normalize();
            up = BABYLON.Vector3.TransformNormal(baseUp, rotationMatrix);
        } else {
            // Standard lens: use projector rotation
            direction = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, 1), rotationMatrix);
            up = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 1, 0), rotationMatrix);
        }
        
        const lensShiftH = (projector.lens_shift_h || 0) / 100;
        // UST auto-adds +70% vertical bias, Periscope auto-adds +45% so the
        // projection lands HIGH on the wall (matches real projector behavior).
        // User's lens_shift_v stays additive on top.
        let autoVBias = 0;
        if (projector.lensType === 'ust') autoVBias = 70;
        else if (projector.lensType === 'topBottom') {
          // Periscope: +45% UP for top mount, -45% DOWN for bottom mount.
          autoVBias = (projector.topBottomDirection === 'bottom') ? -45 : 45;
        }
        const lensShiftV = ((projector.lens_shift_v || 0) + autoVBias) / 100;
        
        const throwDistance = projector.throw_distance || 10;
        const target = position.add(direction.scale(throwDistance));
        
        const viewMatrix = BABYLON.Matrix.LookAtLH(position, target, up);
        
        const aspectRatio = ASPECT_RATIOS[projector.aspect_ratio || '16:9']?.value || 16/9;
        const throwRatio = projector.throw_ratio || 1.5;
        const zoom = (projector.zoom || 100) / 100;
        
        // Throw Ratio = Distance / Width (horizontal)
        // Width = Distance / ThrowRatio
        // Height = Width / AspectRatio = Distance / (ThrowRatio * AspectRatio)
        // Vertical half-angle = atan(Height/2 / Distance) = atan(1 / (2 * ThrowRatio * AspectRatio))
        // But zoom affects the effective throw ratio
        const effectiveThrowRatio = throwRatio * zoom;
        const fovY = 2 * Math.atan(1 / (2 * effectiveThrowRatio * aspectRatio));
        
        const nearPlane = 0.01;    // 1cm - clips wall geometry at lens face, prevents self-occlusion
        const farPlane = 10000;    // 10km - supports very far projections
        
        let projMatrix;
        
        if (lensShiftH !== 0 || lensShiftV !== 0) {
            // Off-axis projection for lens shift
            // Calculate the frustum dimensions at near plane
            const halfHeight = nearPlane * Math.tan(fovY / 2);
            const halfWidth = halfHeight * aspectRatio;
            
            // Apply lens shift as percentage offset of the projection size
            const shiftX = lensShiftH * halfWidth * 2;
            const shiftY = lensShiftV * halfHeight * 2;
            
            // Create off-axis frustum bounds
            const left = -halfWidth + shiftX;
            const right = halfWidth + shiftX;
            const bottom = -halfHeight + shiftY;
            const top = halfHeight + shiftY;
            
            // Create off-axis projection matrix manually (Left-Handed)
            projMatrix = this._createOffAxisProjectionMatrixLH(left, right, bottom, top, nearPlane, farPlane);
        } else {
            // Standard symmetric projection (no lens shift)
            projMatrix = BABYLON.Matrix.PerspectiveFovLH(fovY, aspectRatio, nearPlane, farPlane);
        }
        
        return viewMatrix.multiply(projMatrix);
    }
    
    /**
     * Creates an off-axis perspective projection matrix (Left-Handed coordinate system)
     * Used for lens shift functionality
     */
    _createOffAxisProjectionMatrixLH(left, right, bottom, top, near, far) {
        const matrix = new BABYLON.Matrix();
        const m = matrix.m;
        
        // Off-axis perspective projection matrix (Left-Handed)
        m[0] = (2 * near) / (right - left);
        m[1] = 0;
        m[2] = 0;
        m[3] = 0;
        
        m[4] = 0;
        m[5] = (2 * near) / (top - bottom);
        m[6] = 0;
        m[7] = 0;
        
        m[8] = (left + right) / (left - right);
        m[9] = (top + bottom) / (bottom - top);
        m[10] = far / (far - near);
        m[11] = 1;
        
        m[12] = 0;
        m[13] = 0;
        m[14] = -(near * far) / (far - near);
        m[15] = 0;
        
        matrix.markAsUpdated();
        return matrix;
    }
    
    _getProjectorTexture(projector) {
        const id = projector.id;
        
        if (!this.projectorTextures.has(id)) {
            const texture = new BABYLON.DynamicTexture(
                `projTex_${id}`,
                { width: PROJECTION_TEXTURE_SIZE, height: PROJECTION_TEXTURE_SIZE },
                this.scene,
                true
            );
            texture.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
            texture.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
            this.projectorTextures.set(id, texture);
        }
        
        // Check if projector has a custom image URL
        if (projector.projector_image) {
            const cached = this._customImageCache.get(id);
            
            // Load image if not cached OR if URL changed
            if (!cached || cached.imageUrl !== projector.projector_image) {
                logger.log(`ProjectionSystem: Loading custom image for projector ${id}`);
                this._loadAndCacheImage(id, projector.projector_image);
            }
        } else {
            // Image was removed - clear from cache
            if (this._customImageCache.has(id)) {
                this._customImageCache.delete(id);
                logger.log(`ProjectionSystem: Cleared custom image for projector ${id}`);
            }
        }

        // Video source sync for simple mode — same logic as complex mode.
        const prevVideo = this._customVideoCache.get(id);
        const nextVideoUrl = projector.projector_video || null;
        if ((prevVideo && prevVideo.videoUrl) !== nextVideoUrl) {
            this.loadCustomVideo(id, nextVideoUrl);
        }

        const texture = this.projectorTextures.get(id);
        this._drawProjectorPattern(texture, projector);
        return texture;
    }
    
    _loadAndCacheImage(projectorId, imageUrl) {
        // Skip loading from different/old domains to prevent cross-origin errors
        // But always allow data URIs and URLs that match the configured backend
        if (!imageUrl.startsWith('data:')) {
            try {
                const imgHost = new URL(imageUrl).host;
                const curHost = window.location.host;
                const backendHost = (() => {
                    try { return new URL(process.env.REACT_APP_BACKEND_URL || '').host; } catch { return ''; }
                })();
                if (imgHost !== curHost && imgHost !== backendHost) {
                    logger.warn(`ProjectionSystem: Skipping image from old domain (${imgHost})`);
                    return;
                }
            } catch { /* relative URL — keep it */ }
        }
        
        // Avoid duplicate loads - mark as loading
        const existingCache = this._customImageCache.get(projectorId);
        if (existingCache && existingCache.loading && existingCache.imageUrl === imageUrl) {
            return; // Already loading this URL
        }
        
        // Mark as loading
        this._customImageCache.set(projectorId, { imageUrl, loading: true, img: null });
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            // Store in persistent cache
            this._customImageCache.set(projectorId, { 
                imageUrl, 
                img, 
                loading: false 
            });
            logger.log(`ProjectionSystem: Custom image loaded and cached for projector ${projectorId}`);
            
            // Immediately redraw the texture
            const texture = this.projectorTextures.get(projectorId);
            const projector = this.projectors.find(p => p.id === projectorId);
            if (texture && projector) {
                this._drawProjectorPattern(texture, projector);
            }
            
            // Update atlas if in complex mode
            if (this.mode === 'complex' && projector) {
                const index = this.projectors.indexOf(projector);
                if (index >= 0) {
                    this._updateAtlasSlot(index, projector);
                    this._uploadAtlasToRawTexture();
                }
            }
        };
        img.onerror = () => {
            logger.error(`ProjectionSystem: Failed to load custom image for projector ${projectorId}`);
            this._customImageCache.delete(projectorId);
        };
        img.src = imageUrl;
    }
    
    _drawProjectorPattern(texture, projector, videoFrame = null) {
        const ctx = texture.getContext();
        const size = PROJECTION_TEXTURE_SIZE;
        const id = projector.id;

        // User-controlled texture transform (UI: TEXTURE CONTROL panel).
        // 100 == 1.0 scale, offset is a percentage of the texture size.
        const sx = (projector.textureScaleX ?? 100) / 100;
        const sy = (projector.textureScaleY ?? 100) / 100;
        const tx = ((projector.textureOffsetX ?? 0) / 100) * size;
        const ty = ((projector.textureOffsetY ?? 0) / 100) * size;
        const hasXform = sx !== 1 || sy !== 1 || tx !== 0 || ty !== 0;

        // Always start clean so empty area outside scaled-down content
        // doesn't leave stale pixels.
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, size, size);
        ctx.restore();

        // Apply user transform: scale around center, then offset.
        if (hasXform) {
            ctx.save();
            ctx.translate(size / 2 + tx, size / 2 + ty);
            ctx.scale(sx, sy);
            ctx.translate(-size / 2, -size / 2);
        }

        // Check persistent cache for custom image
        const cached = this._customImageCache.get(id);
        const hasCustomImage = cached && cached.img && !cached.loading && projector.projector_image;

        if (videoFrame) {
            // Live video frame takes priority over a static image/test pattern.
            // The projection shader samples this texture with `1.0 - uv.y`,
            // so we draw the source pre-flipped on the canvas to keep it
            // right-side up on the projected surface.
            ctx.save();
            ctx.translate(0, size);
            ctx.scale(1, -1);
            ctx.drawImage(videoFrame, 0, 0, size, size);
            ctx.restore();
        } else if (hasCustomImage) {
            // Same pre-flip as videos so uploaded images project right-side up.
            ctx.save();
            ctx.translate(0, size);
            ctx.scale(1, -1);
            ctx.drawImage(cached.img, 0, 0, size, size);
            ctx.restore();
        } else if (projector.test_pattern_on && projector.test_pattern_id) {
            this._drawTestPattern(ctx, 0, 0, size, projector.test_pattern_id);
        } else {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, size, size);
            this._drawGridToContext(ctx, 0, 0, size);
        }

        if (hasXform) {
            ctx.restore();
        }

        // Border is drawn AFTER the texture transform so it always frames
        // the projection beam (not the inner content).
        if (projector.show_border === true) {
            const borderColor = projector.color || '#ff00ff';
            ctx.strokeStyle = borderColor;
            // Per-projector configurable border width (default: 2px thin)
            ctx.lineWidth = projector.border_width || 2;
            ctx.strokeRect(ctx.lineWidth / 2, ctx.lineWidth / 2, size - ctx.lineWidth, size - ctx.lineWidth);
        }
        
        texture.update();
    }
    
    _updateAtlasSlot(index, projector, videoFrame = null) {
        const ctx = this._atlasCtx;
        const id = projector.id;
        
        const col = index % this.atlasColumns;
        const row = Math.floor(index / this.atlasColumns);
        const x = col * this.patternSize;
        const y = row * this.patternSize;

        // User-controlled texture transform (TEXTURE CONTROL panel).
        const sx = (projector.textureScaleX ?? 100) / 100;
        const sy = (projector.textureScaleY ?? 100) / 100;
        const tx = ((projector.textureOffsetX ?? 0) / 100) * this.patternSize;
        const ty = ((projector.textureOffsetY ?? 0) / 100) * this.patternSize;
        const hasXform = sx !== 1 || sy !== 1 || tx !== 0 || ty !== 0;

        // Clip to this slot so we never bleed into neighbours, and clear it.
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, this.patternSize, this.patternSize);
        ctx.clip();
        ctx.fillStyle = '#000000';
        ctx.fillRect(x, y, this.patternSize, this.patternSize);

        if (hasXform) {
            ctx.translate(x + this.patternSize / 2 + tx, y + this.patternSize / 2 + ty);
            ctx.scale(sx, sy);
            ctx.translate(-(x + this.patternSize / 2), -(y + this.patternSize / 2));
        }

        // Check persistent cache for custom image
        const cached = this._customImageCache.get(id);
        const hasCustomImage = cached && cached.img && !cached.loading && projector.projector_image;

        if (videoFrame) {
            // Pre-flip on canvas so the projection shader's `1.0 - uv.y`
            // sample comes out right-side up on the projected surface.
            ctx.save();
            ctx.translate(x, y + this.patternSize);
            ctx.scale(1, -1);
            ctx.drawImage(videoFrame, 0, 0, this.patternSize, this.patternSize);
            ctx.restore();
        } else if (hasCustomImage) {
            ctx.save();
            ctx.translate(x, y + this.patternSize);
            ctx.scale(1, -1);
            ctx.drawImage(cached.img, 0, 0, this.patternSize, this.patternSize);
            ctx.restore();
        } else if (projector.test_pattern_on && projector.test_pattern_id) {
            this._drawTestPattern(ctx, x, y, this.patternSize, projector.test_pattern_id);
        } else {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(x, y, this.patternSize, this.patternSize);
            this._drawGridToContext(ctx, x, y, this.patternSize);
        }

        ctx.restore();
        
        if (projector.show_border === true) {
            const borderColor = projector.color || '#ff00ff';
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = projector.border_width || 2;
            ctx.strokeRect(x + ctx.lineWidth / 2, y + ctx.lineWidth / 2, this.patternSize - ctx.lineWidth, this.patternSize - ctx.lineWidth);
        }
    }
    
    // Keep loadCustomImage as a public API that uses the new cache system
    loadCustomImage(projectorId, imageUrl) {
        this._loadAndCacheImage(projectorId, imageUrl);
    }

    // ============================================================
    // VIDEO SOURCE SUPPORT
    // ============================================================
    // A projector can specify `projector_video` (a URL to an optimized MP4).
    // The actual <video> element is owned by VideoTextureManager so multiple
    // projectors using the SAME url share a single decode pass (automatic
    // global sync — every projector sees the same frame every tick).
    //
    // Each frame, if any projector has an active video source, we redraw the
    // atlas slot (or per-projector DynamicTexture in simple mode) by blitting
    // the current video frame into the existing canvas. This keeps the
    // projection shader unchanged — it still samples from the same sampler,
    // just with fresh pixels every frame.
    loadCustomVideo(projectorId, videoUrl) {
        const prev = this._customVideoCache.get(projectorId);
        if (prev && prev.videoUrl === videoUrl) return;
        // Release the previous source (refcount decrement)
        if (prev && prev.videoUrl) {
            releaseVideo(prev.videoUrl, projectorId);
        }
        if (!videoUrl) {
            this._customVideoCache.delete(projectorId);
            return;
        }
        acquireVideo(videoUrl, projectorId);
        this._customVideoCache.set(projectorId, { videoUrl });
        this._ensureVideoTick();
    }

    clearCustomVideo(projectorId) {
        this.loadCustomVideo(projectorId, null);
    }

    /**
     * Per-frame hook — called once per render tick to refresh atlas / per-
     * projector textures whenever at least one projector has an active video
     * source. Registered lazily to avoid overhead when no videos are in use.
     */
    _ensureVideoTick() {
        if (this._videoTickObserver) return;
        if (!this.scene || !this.scene.onBeforeRenderObservable) return;
        this._videoTickObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (this._customVideoCache.size === 0) return;
            // Iterate known projectors — for each one that has a video source,
            // redraw its slot / texture. drawImage(videoEl, …) is essentially
            // a GPU-backed blit in all modern browsers.
            for (const [projectorId, entry] of this._customVideoCache) {
                const video = getVideoForProjector(projectorId, entry.videoUrl);
                if (!video) continue;
                const projector = this.projectors.find(p => p.id === projectorId);
                if (!projector) continue;
                if (this.mode === 'complex') {
                    const index = this.projectors.indexOf(projector);
                    if (index >= 0) {
                        this._updateAtlasSlot(index, projector, video);
                    }
                } else {
                    const texture = this.projectorTextures.get(projectorId);
                    if (texture) this._drawProjectorPattern(texture, projector, video);
                }
            }
            if (this.mode === 'complex') {
                this._uploadAtlasToRawTexture();
            }
        });
    }
    
    createSimpleMaterial(meshId, baseColor) {
        const material = new BABYLON.ShaderMaterial(
            'projSimple_' + meshId,
            this.scene,
            { vertex: 'projSimple', fragment: 'projSimple' },
            {
                attributes: ['position', 'normal', 'uv'],
                uniforms: [
                    'worldViewProjection', 'world', 'baseColor', 'ambientIntensity', 'numProjectors', 'materialAlpha',
                    'hasObjectTexture', 'objectUvMappingEnabled', 'objectTextureBrightness', 'objectTextureContrast', 'objectTextureSaturation', 'debugShadowMode', 'scrimAttenuation', 'overlapBrightness',
                    'useObjectTriplanar', 'objectTriplanarScale',
                    'proj0Pos', 'proj0VP', 'proj0Brightness', 'proj0Power', 'proj0IsReverse', 'proj0Border',
                    'proj1Pos', 'proj1VP', 'proj1Brightness', 'proj1Power', 'proj1IsReverse', 'proj1Border',
                    'proj2Pos', 'proj2VP', 'proj2Brightness', 'proj2Power', 'proj2IsReverse', 'proj2Border',
                    'proj3Pos', 'proj3VP', 'proj3Brightness', 'proj3Power', 'proj3IsReverse', 'proj3Border',
                    'proj4Pos', 'proj4VP', 'proj4Brightness', 'proj4Power', 'proj4IsReverse', 'proj4Border',
                    'proj5Pos', 'proj5VP', 'proj5Brightness', 'proj5Power', 'proj5IsReverse', 'proj5Border',
                    'proj6Pos', 'proj6VP', 'proj6Brightness', 'proj6Power', 'proj6IsReverse', 'proj6Border'
                ],
                samplers: [
                    'objectDiffuseTexture', 'shadowAtlas',
                    'proj0Texture', 'proj1Texture', 'proj2Texture', 'proj3Texture',
                    'proj4Texture', 'proj5Texture', 'proj6Texture'
                ],
                uniformBuffers: ['Scene', 'Mesh']
            }
        );
        
        material.setColor3('baseColor', baseColor);
        material.setFloat('ambientIntensity', 0.4);
        material.setInt('numProjectors', 0);
        material.setFloat('materialAlpha', 1.0);
        material.setFloat('debugShadowMode', 0.0);
        material.setFloat('scrimAttenuation', 0.0);
        material.setFloat('overlapBrightness', this._overlapBrightness);
        
        // Shadow atlas texture
        if (this.shadowAtlasRTT) {
            material.setTexture('shadowAtlas', this.shadowAtlasRTT);
        }
        
        // Object texture uniforms for reverse projection
        material.setTexture('objectDiffuseTexture', this.defaultTextures[0]);
        material.setFloat('hasObjectTexture', 0.0);
        material.setFloat('objectUvMappingEnabled', 0.0);
        material.setFloat('objectTextureBrightness', 1.0);
        material.setFloat('objectTextureContrast', 1.0);
        material.setFloat('objectTextureSaturation', 1.0);
        // Universal Texture Mapping — disabled by default so behaviour is
        // unchanged for every existing receiver mesh. Coordinator flips
        // this flag per-mesh when the user picks Triplanar.
        material.setFloat('useObjectTriplanar', 0.0);
        material.setFloat('objectTriplanarScale', 1.0);
        
        for (let i = 0; i < this.maxSimpleProjectors; i++) {
            material.setVector3(`proj${i}Pos`, BABYLON.Vector3.Zero());
            material.setMatrix(`proj${i}VP`, BABYLON.Matrix.Identity());
            material.setFloat(`proj${i}Brightness`, 1.0);
            material.setFloat(`proj${i}Power`, 0.0);
            material.setTexture(`proj${i}Texture`, this.defaultTextures[i] || this.defaultTextures[0]);
            material.setFloat(`proj${i}IsReverse`, 0.0);
        }
        
        material.backFaceCulling = false;
        // Only enable alpha blending if explicitly needed (for scrim materials)
        // Default to opaque to prevent transparency issues
        material.alphaMode = BABYLON.Engine.ALPHA_DISABLE;
        
        return material;
    }
    
    createComplexMaterial(meshId, baseColor) {
        const material = new BABYLON.ShaderMaterial(
            'projComplex_' + meshId,
            this.scene,
            { vertex: 'projComplex', fragment: 'projComplex' },
            {
                attributes: ['position', 'normal', 'uv'],
                uniforms: [
                    'worldViewProjection', 'world', 'baseColor', 'ambientIntensity', 
                    'numProjectors', 'dataTexWidth', 'materialAlpha', 'scrimAttenuation', 'overlapBrightness',
                    'hasObjectTexture', 'objectTextureBrightness', 'objectTextureContrast', 'objectTextureSaturation',
                    'useObjectTriplanar', 'objectTriplanarScale'
                ],
                samplers: [
                    'projectorData', 'projectionAtlas', 'shadowAtlas',
                    'objectDiffuseTexture'
                ],
                uniformBuffers: ['Scene', 'Mesh']
            }
        );
        
        material.setColor3('baseColor', baseColor);
        material.setFloat('ambientIntensity', 0.4);
        material.setInt('numProjectors', 0);
        material.setFloat('dataTexWidth', DATA_TEX_WIDTH);
        material.setFloat('materialAlpha', 1.0);
        material.setFloat('scrimAttenuation', 0.0);
        material.setFloat('overlapBrightness', this._overlapBrightness);
        
        // Shadow atlas texture
        if (this.shadowAtlasRTT) {
            material.setTexture('shadowAtlas', this.shadowAtlasRTT);
        }
        
        // Object texture uniforms for reverse projection
        material.setTexture('objectDiffuseTexture', this.defaultTextures[0]);
        material.setFloat('hasObjectTexture', 0.0);
        material.setFloat('objectTextureBrightness', 1.0);
        material.setFloat('objectTextureContrast', 1.0);
        material.setFloat('objectTextureSaturation', 1.0);
        // Universal Texture Mapping (triplanar) — disabled by default; the
        // coordinator flips this on per mesh when the user picks Triplanar.
        material.setFloat('useObjectTriplanar', 0.0);
        material.setFloat('objectTriplanarScale', 1.0);
        
        material.setTexture('projectorData', this.projectorDataTexture);
        material.setTexture('projectionAtlas', this.atlasRawTexture);
        
        // Disable backface culling to allow double-sided meshes (like domes) to render both surfaces
        // The shader's gl_FrontFacing logic determines which side receives projection based on projector position
        material.backFaceCulling = false;
        // Only enable alpha blending if explicitly needed (for scrim materials)
        // Default to opaque to prevent transparency issues
        material.alphaMode = BABYLON.Engine.ALPHA_DISABLE;
        
        return material;
    }
    
    registerReceiver(mesh, originalMaterial = null) {
        if (!mesh) return;
        
        const meshId = mesh.uniqueId || mesh.name;
        if (this.receiverMeshes.has(meshId)) return;
        
        if (!originalMaterial && mesh.material) {
            originalMaterial = mesh.material;
        }
        
        let baseColor = new BABYLON.Color3(0.5, 0.5, 0.5);
        let materialAlpha = 1.0;  // Default to fully opaque
        
        if (originalMaterial?.diffuseColor) {
            baseColor = originalMaterial.diffuseColor.clone();
        } else if (originalMaterial?.albedoColor) {
            baseColor = originalMaterial.albedoColor.clone();
        }
        
        // Check if this is a scrim screen (preserve alpha/transparency)
        if (originalMaterial?.alpha !== undefined && originalMaterial.alpha < 1.0) {
            materialAlpha = originalMaterial.alpha;
        }
        // Also check mesh metadata for scrim info
        if (mesh.metadata?.isScrim && mesh.metadata?.scrimOpacity !== undefined) {
            materialAlpha = mesh.metadata.scrimOpacity;
        }
        
        const projMaterial = this.mode === 'complex'
            ? this.createComplexMaterial(meshId, baseColor)
            : this.createSimpleMaterial(meshId, baseColor);
        
        // Set the alpha for scrim screens
        projMaterial.setFloat('materialAlpha', materialAlpha);
        
        // ONLY enable alpha blending for transparent materials (scrims)
        // This prevents selection/highlight issues with opaque objects
        if (materialAlpha < 1.0) {
            projMaterial.backFaceCulling = false;
            projMaterial.alphaMode = BABYLON.Engine.ALPHA_COMBINE;
            // Enable alpha blending ONLY for transparent materials
            projMaterial.needAlphaBlending = function() { return true; };
            projMaterial.needAlphaTesting = function() { return false; };
        }
        
        // Check if this is a scrim screen (transparent to projection light)
        const isScrim = mesh.metadata?.isScrim || materialAlpha < 1.0;
        
        this.receiverMeshes.set(meshId, {
            mesh,
            material: projMaterial,
            originalMaterial,
            baseColor,
            materialAlpha,  // Store alpha for updates
            isScrim,        // Track scrim status
            objectTexture: null  // For reverse projection
        });
        
        mesh.material = projMaterial;
        
        // Check if mesh has existing texture data (from loaded state) and apply it
        const existingTextureUrl = mesh.metadata?.objectData?.textureUrl || mesh.metadata?.textureUrl;
        if (existingTextureUrl) {
            logger.log(`ProjectionSystem: Mesh ${mesh.name} has existing texture, applying: ${existingTextureUrl.substring(0, 50)}...`);
            // Apply texture after a short delay to ensure material is ready
            setTimeout(() => {
                this.updateReceiverObjectTexture(mesh, existingTextureUrl);
            }, 100);
        }
        
        // SCRIM LIGHT TRANSMISSION: Scrim screens should NOT be occluders
        // This allows projection light to pass through the scrim to objects behind
        // Scrim screens ARE receivers (they show the projection on their surface)
        // but they are NOT occluders (they don't block light for shadow calculation)
        if (!isScrim) {
            this.occluderMeshes.set(meshId, mesh);
            this._occluderVersion = (this._occluderVersion || 0) + 1;
            logger.log(`ProjectionSystem: Registered receiver/occluder: ${mesh.name} (alpha: ${materialAlpha})`);
        } else {
            // Scrim is only a receiver, not an occluder - light passes through
            logger.log(`ProjectionSystem: Registered SCRIM receiver (light pass-through): ${mesh.name} (alpha: ${materialAlpha})`);
        }
    }
    
    /**
     * Update scrim attenuation on all non-scrim receivers.
     * For each non-scrim receiver, checks if any scrim is between it and any projector.
     * If so, attenuates the projection by the scrim's opacity.
     * @param {number} scrimOpacity - The scrim opacity (0=transparent, 1=opaque)
     */
    updateScrimAttenuation(scrimOpacity) {
        // Find all scrim receivers and their positions
        const scrims = [];
        for (const [, entry] of this.receiverMeshes) {
            if (entry.isScrim && entry.mesh && !entry.mesh.isDisposed()) {
                scrims.push({
                    mesh: entry.mesh,
                    position: entry.mesh.getAbsolutePosition(),
                    opacity: scrimOpacity
                });
            }
        }
        
        if (scrims.length === 0) {
            // No scrims — clear attenuation on all receivers
            for (const [, entry] of this.receiverMeshes) {
                if (entry.material && !entry.isScrim) {
                    entry.material.setFloat('scrimAttenuation', 0.0);
                }
            }
            return;
        }
        
        // Get all projector positions
        const projPositions = [];
        for (const proj of this.projectors) {
            if (proj.position) {
                // Use lens front face as origin, not body center
                const bodyD = (proj.body_depth || proj.spec?.depth || 600) / 1000;
                const rX = (proj.rotation?.x || 0) * Math.PI / 180;
                const rY = (proj.rotation?.y || 0) * Math.PI / 180;
                const rZ = (proj.rotation?.z || 0) * Math.PI / 180;
                const rMat = BABYLON.Matrix.RotationYawPitchRoll(rY, rX, rZ);
                const fwd = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, 1), rMat).normalize();
                const base = new BABYLON.Vector3(proj.position.x, proj.position.y, proj.position.z);
                projPositions.push(base.add(fwd.scale(bodyD / 2)));
            }
        }
        if (projPositions.length === 0) return;
        
        // For each non-scrim receiver, check if it's behind any scrim from any projector
        for (const [, entry] of this.receiverMeshes) {
            if (entry.isScrim || !entry.material || !entry.mesh || entry.mesh.isDisposed()) continue;
            
            const receiverPos = entry.mesh.getAbsolutePosition();
            let maxAttenuation = 0;
            
            for (const projPos of projPositions) {
                const projToReceiver = receiverPos.subtract(projPos);
                const distToReceiver = projToReceiver.length();
                
                for (const scrim of scrims) {
                    const projToScrim = scrim.position.subtract(projPos);
                    const distToScrim = projToScrim.length();
                    
                    // Scrim must be between projector and receiver (closer to projector)
                    if (distToScrim >= distToReceiver) continue;
                    
                    // Check if scrim is roughly in the path (dot product > 0.3 = ~72 degrees)
                    const dirToReceiver = projToReceiver.normalize();
                    const dirToScrim = projToScrim.normalize();
                    const dot = BABYLON.Vector3.Dot(dirToReceiver, dirToScrim);
                    
                    if (dot > 0.3) {
                        maxAttenuation = Math.max(maxAttenuation, scrim.opacity);
                    }
                }
            }
            
            entry.material.setFloat('scrimAttenuation', maxAttenuation);
        }
    }
    
    /**
     * Update object texture for reverse projection mode
     * @param {BABYLON.Mesh} mesh - The mesh to update
     * @param {string} textureUrl - The texture URL or null to clear
     */
    updateReceiverObjectTexture(mesh, textureUrl) {
        if (!mesh) {
            logger.warn('updateReceiverObjectTexture: mesh is null');
            return;
        }
        
        // Skip loading textures from different/old domains to prevent cross-origin errors
        // But always allow data URIs and URLs that match the configured backend
        if (textureUrl && !textureUrl.startsWith('data:')) {
            try {
                const texHost = new URL(textureUrl).host;
                const curHost = window.location.host;
                // Also allow the configured backend host (for custom domain deployments)
                const backendHost = (() => {
                    try { return new URL(process.env.REACT_APP_BACKEND_URL || '').host; } catch { return ''; }
                })();
                if (texHost !== curHost && texHost !== backendHost) {
                    logger.warn(`ProjectionSystem: Skipping texture from old domain (${texHost}): ${textureUrl.substring(0, 60)}`);
                    textureUrl = null;
                }
            } catch { /* relative URL — keep it */ }
        }
        
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        
        logger.log(`updateReceiverObjectTexture called for ${mesh.name} (id: ${meshId}), textureUrl: ${textureUrl ? 'yes (' + textureUrl.substring(0, 50) + '...)' : 'null'}, entry found: ${!!entry}`);
        
        if (entry) {
            // Dispose old texture if exists
            if (entry.objectTexture) {
                entry.objectTexture.dispose();
                entry.objectTexture = null;
            }
            
            // Load new texture if URL provided
            if (textureUrl) {
                logger.log(`ProjectionSystem: Loading object texture for ${mesh.name}`);
                const texture = new BABYLON.Texture(
                    textureUrl, this.scene, false, true, BABYLON.Texture.TRILINEAR_SAMPLINGMODE,
                    null, // onLoad handled below
                    () => {
                        // onError — silently handle failed texture loads (e.g. old/deleted URLs)
                        logger.warn(`ProjectionSystem: Failed to load texture for ${mesh.name}: ${textureUrl.substring(0, 60)}`);
                        if (entry.material) {
                            entry.material.setFloat('hasObjectTexture', 0.0);
                        }
                        entry.objectTexture = null;
                    }
                );
                texture.hasAlpha = false;
                texture.wrapU = BABYLON.Texture.WRAP_ADDRESSMODE;
                texture.wrapV = BABYLON.Texture.WRAP_ADDRESSMODE;
                entry.objectTexture = texture;
                
                // Wait for texture to load before setting uniforms
                texture.onLoadObservable.addOnce(() => {
                    logger.log(`ProjectionSystem: Texture LOADED for ${mesh.name}, isReady: ${texture.isReady()}`);
                    if (entry.material) {
                        entry.material.setTexture('objectDiffuseTexture', texture);
                        entry.material.setFloat('hasObjectTexture', 1.0);
                        logger.log(`ProjectionSystem: Set hasObjectTexture=1.0 for ${mesh.name}`);
                    }
                });
                
                // Also set immediately in case texture is already cached
                if (entry.material) {
                    entry.material.setTexture('objectDiffuseTexture', texture);
                    entry.material.setFloat('hasObjectTexture', 1.0);
                }
                logger.log(`ProjectionSystem: Updated object texture for reverse projection: ${mesh.name}`);
            } else {
                if (entry.material) {
                    entry.material.setFloat('hasObjectTexture', 0.0);
                }
            }
        } else {
            logger.warn(`ProjectionSystem: No entry found for mesh ${mesh.name} (id: ${meshId})`);
            logger.log('Available entries:', Array.from(this.receiverMeshes.keys()));
        }
    }
    
    /**
     * Update object texture settings (brightness, contrast, saturation) for a receiver mesh
     */
    updateReceiverTextureSettings(mesh, settings) {
        if (!mesh) return;
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        if (entry && entry.material) {
            if (settings.brightness !== undefined) {
                entry.material.setFloat('objectTextureBrightness', (settings.brightness ?? 100) / 100);
            }
            if (settings.contrast !== undefined) {
                entry.material.setFloat('objectTextureContrast', (settings.contrast ?? 100) / 100);
            }
            if (settings.saturation !== undefined) {
                entry.material.setFloat('objectTextureSaturation', (settings.saturation ?? 100) / 100);
            }
        }
    }
    
    /**
     * Update object UV mapping enabled flag for reverse projection
     * @param {BABYLON.Mesh} mesh - The mesh to update
     * @param {boolean} enabled - Whether UV mapping is enabled
     */
    updateReceiverUvMapping(mesh, enabled) {
        if (!mesh) return;
        
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        
        if (entry && entry.material) {
            entry.material.setFloat('objectUvMappingEnabled', enabled ? 1.0 : 0.0);
        }
    }

    /**
     * Universal Texture Mapping — toggle triplanar sampling of the object's
     * diffuse texture for a receiver mesh. Zero-cost when `enabled=false`
     * (one uniform branch test). When enabled, replaces UV lookup with
     * world-space triplanar blend in the projection shader so meshes with
     * missing/broken UVs (domes, spheres, collapsed-UV imports) render
     * textures without stretching.
     *
     * @param {BABYLON.Mesh} mesh
     * @param {boolean} enabled
     * @param {number} scale  world-space tile scale (0.1 – 5, default 1)
     */
    updateReceiverTriplanar(mesh, enabled, scale = 1.0) {
        if (!mesh) return;
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        if (entry && entry.material) {
            entry.material.setFloat('useObjectTriplanar', enabled ? 1.0 : 0.0);
            entry.material.setFloat('objectTriplanarScale', Math.max(0.001, Number(scale) || 1.0));
        }
    }
    
    // Update the alpha/transparency of a registered receiver (for scrim screens)
    updateReceiverAlpha(mesh, newAlpha) {
        if (!mesh) return;
        
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        
        if (entry && entry.material) {
            entry.material.setFloat('materialAlpha', newAlpha);
            entry.materialAlpha = newAlpha;
            
            // Disable backface culling for transparent materials
            if (newAlpha < 1.0) {
                entry.material.backFaceCulling = false;
            }
        }
    }
    
    /**
     * Update the base color of a registered receiver mesh
     * @param {BABYLON.Mesh} mesh - The mesh to update
     * @param {BABYLON.Color3} newColor - The new color to set
     */
    updateReceiverColor(mesh, newColor) {
        if (!mesh) return;
        
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        
        if (entry && entry.material) {
            entry.material.setColor3('baseColor', newColor);
            entry.baseColor = newColor.clone();
        }
    }
    
    unregisterReceiver(mesh) {
        if (!mesh) return;
        
        const meshId = mesh.uniqueId || mesh.name;
        const entry = this.receiverMeshes.get(meshId);
        
        if (entry) {
            if (entry.originalMaterial) {
                mesh.material = entry.originalMaterial;
            }
            if (entry.material) {
                entry.material.dispose();
            }
            this.receiverMeshes.delete(meshId);
            if (this.occluderMeshes.has(meshId)) {
                this.occluderMeshes.delete(meshId);
                this._occluderVersion = (this._occluderVersion || 0) + 1;
            }
        }
    }
    
    /**
     * Register a mesh as an occluder ONLY (blocks projection light, but does NOT receive projections).
     * Used for projector body meshes — they should cast shadows but not display projected content.
     * @param {BABYLON.Mesh} mesh - The mesh to register as an occluder
     * @param {string} [ownerProjectorId] - The projector ID that owns this mesh (to exclude self-shadowing)
     */
    registerOccluderOnly(mesh, ownerProjectorId = null) {
        if (!mesh) return;
        const meshId = mesh.uniqueId || mesh.name;
        if (this.occluderMeshes.has(meshId)) return;
        this.occluderMeshes.set(meshId, mesh);
        if (ownerProjectorId) {
            if (!this._projectorOccluderIds) this._projectorOccluderIds = new Map();
            this._projectorOccluderIds.set(meshId, ownerProjectorId);
        }
        this._occluderVersion = (this._occluderVersion || 0) + 1;
        logger.log(`ProjectionSystem: Registered occluder-only: ${mesh.name}${ownerProjectorId ? ` (owner: ${ownerProjectorId})` : ''}`);
    }
    
    /**
     * Unregister an occluder-only mesh (does not touch receivers).
     */
    unregisterOccluder(mesh) {
        if (!mesh) return;
        const meshId = mesh.uniqueId || mesh.name;
        if (this.occluderMeshes.has(meshId)) {
            this.occluderMeshes.delete(meshId);
            if (this._projectorOccluderIds) this._projectorOccluderIds.delete(meshId);
            this._occluderVersion = (this._occluderVersion || 0) + 1;
            logger.log(`ProjectionSystem: Unregistered occluder: ${mesh.name}`);
        }
    }
    
    /**
     * Get a fingerprint of projector's shadow-relevant properties for dirty tracking
     */
    _getProjectorFingerprint(projector) {
        const p = projector.position || {};
        const r = projector.rotation || {};
        return (p.x||0)+','+(p.y||0)+','+(p.z||0)+','+(r.x||0)+','+(r.y||0)+','+(r.z||0)+','+(projector.throw_ratio||0)+','+(projector.zoom||0)+','+(projector.lens_shift_h||0)+','+(projector.lens_shift_v||0)+','+(projector.aspect_ratio||'');
    }
    
    /**
     * Update shadow atlas - renders depth maps into atlas tiles
     * Uses dirty tile tracking to skip unchanged projectors in static scenes
     */
    renderShadowMaps() {
        if (!this.shadowAtlasRTT) return;
        
        let needsUpdate = false;
        
        // Check if occluders changed (forces full re-render)
        if (this._occluderVersion !== this._lastOccluderVersion) {
            needsUpdate = true;
            this._lastOccluderVersion = this._occluderVersion;
        }
        
        // Check each projector for changes
        const numProj = Math.min(this.projectors.length, MAX_SHADOW_PROJECTORS);
        for (let i = 0; i < numProj; i++) {
            const projector = this.projectors[i];
            const fp = this._getProjectorFingerprint(projector);
            if (fp !== this._projectorFingerprints[i]) {
                this._projectorFingerprints[i] = fp;
                needsUpdate = true;
            }
            // Update VP matrix
            this._vpMatrices[i] = this._calculateProjectorVPMatrix(projector);
            // Set VP matrix on this projector's DEDICATED depth material
            if (this.depthMaterials[i]) {
                this.depthMaterials[i].setMatrix('shadowViewProjection', this._vpMatrices[i]);
            }
        }
        
        // Clear fingerprints for removed projectors
        for (let i = numProj; i < MAX_SHADOW_PROJECTORS; i++) {
            if (this._projectorFingerprints[i] !== '') {
                this._projectorFingerprints[i] = '';
                needsUpdate = true;
            }
        }
        
        // Force render until atlas has been successfully rendered at least once
        if (!this._atlasRenderedOnce) {
            needsUpdate = true;
        }
        
        // Control atlas refresh rate based on dirty state
        this._shadowNeedsUpdate = needsUpdate;
        this.shadowAtlasRTT.refreshRate = needsUpdate
            ? BABYLON.RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYFRAME
            : 0;
        
        // Update RTT render list with all occluder meshes
        // NOTE: We do NOT use setMaterialForRendering here — the customRenderFunction
        // handles material assignment per-projector via the depthMaterials pool.
        this.shadowAtlasRTT.renderList = [];
        this.occluderMeshes.forEach((mesh, meshId) => {
            if (mesh && !mesh.isDisposed() && mesh.isEnabled() && mesh.isVisible) {
                this.shadowAtlasRTT.renderList.push(mesh);
                
                // For projector bodies, only add the frame child (skip lens parts which are too close to origin)
                const isProjectorBody = this._projectorOccluderIds && this._projectorOccluderIds.has(meshId);
                if (mesh.getChildMeshes) {
                    const children = mesh.getChildMeshes(false);
                    children.forEach(child => {
                        if (child && !child.isDisposed() && child.isEnabled()) {
                            if (isProjectorBody) {
                                // Only include frame meshes for projector bodies, skip lens parts
                                if (child.name && child.name.startsWith('frame_')) {
                                    this.shadowAtlasRTT.renderList.push(child);
                                    // Track frame's owner for self-shadow exclusion
                                    const childId = child.uniqueId || child.name;
                                    const ownerId = this._projectorOccluderIds.get(meshId);
                                    if (ownerId) this._projectorOccluderIds.set(childId, ownerId);
                                }
                            } else {
                                this.shadowAtlasRTT.renderList.push(child);
                            }
                        }
                    });
                }
            }
        });
        
        if (!this._debugSlotLogged) {
            this._debugSlotLogged = true;
            logger.log('[Shadow Atlas] projectors=' + this.projectors.length + ' tiles=' + numProj + ' occluders=' + this.occluderMeshes.size);
        }
    }
    
    
        updateProjectors(projectors) {
        this.projectors = projectors.filter(p => p.power_on);
        
        // When no active projectors, keep shader materials but set numProjectors=0
        // This allows object textures to still be rendered by the shader
        if (this.projectors.length === 0) {
            this.receiverMeshes.forEach((entry) => {
                if (entry.mesh && entry.material) {
                    // Make sure shader material is active (not original material)
                    if (entry.mesh.material !== entry.material) {
                        entry.mesh.material = entry.material;
                    }
                    // Set numProjectors to 0 so shader shows object texture
                    entry.material.setInt('numProjectors', 0);
                    
                    // Update object texture uniforms
                    const objData = entry.mesh.metadata?.objectData || {};
                    const textureBrightness = (objData.textureBrightness ?? 100) / 100;
                    const textureContrast = (objData.textureContrast ?? 100) / 100;
                    const textureSaturation = (objData.textureSaturation ?? 100) / 100;
                    const hasTexture = (objData.textureUrl || entry.objectTexture) ? 1.0 : 0.0;
                    entry.material.setFloat('hasObjectTexture', hasTexture);
                    entry.material.setFloat('objectTextureBrightness', textureBrightness);
                    entry.material.setFloat('objectTextureContrast', textureContrast);
                    entry.material.setFloat('objectTextureSaturation', textureSaturation);
                    
                    // Apply object texture if it exists
                    if (entry.objectTexture) {
                        entry.material.setTexture('objectDiffuseTexture', entry.objectTexture);
                    }
                }
            });
            return;
        }
        
        // Re-apply projection materials if they were previously restored to originals
        this.receiverMeshes.forEach((entry) => {
            if (entry.mesh && entry.material && entry.mesh.material === entry.originalMaterial) {
                entry.mesh.material = entry.material;
            }
        });
        
        const newMode = this.projectors.length > this.maxSimpleProjectors ? 'complex' : 'simple';
        
        if (newMode !== this.mode) {
            this.mode = newMode;
            this._rebuildMaterials();
            logger.log(`ProjectionSystem: Switched to ${this.mode} mode (${this.projectors.length} projectors)`);
        }
        
        const activeIds = new Set(this.projectors.map(p => p.id));
        for (const [id, texture] of this.projectorTextures) {
            if (!activeIds.has(id)) {
                texture.dispose();
                this.projectorTextures.delete(id);
            }
        }
        
        // Render shadow maps first
        this.renderShadowMaps();
        
        // Shadow system debug logging (runs once)
        if (!this._shadowDebugLogged) {
            this._shadowDebugLogged = true;
            logger.log('[Shadow Atlas Debug]', {
                projectors: this.projectors.length,
                numOccluders: this.occluderMeshes.size,
                numReceivers: this.receiverMeshes.size,
                atlasSize: SHADOW_ATLAS_SIZE + 'x' + SHADOW_ATLAS_SIZE,
                tileSize: SHADOW_TILE_SIZE,
                maxTiles: MAX_SHADOW_PROJECTORS
            });
        }
        
        if (this.mode === 'complex') {
            this._updateComplexMode();
        } else {
            this._updateSimpleMode();
        }
        
        if (this.projectors.length > 0) {
            logger.log(`ProjectionSystem: ${this.projectors.length} projectors → ${this.receiverMeshes.size} surfaces (${this.mode} mode, shadows enabled)`);
        }
    }
    
    _rebuildMaterials() {
        this.receiverMeshes.forEach((entry, meshId) => {
            if (entry.material) {
                entry.material.dispose();
            }
            
            const newMaterial = this.mode === 'complex'
                ? this.createComplexMaterial(meshId, entry.baseColor)
                : this.createSimpleMaterial(meshId, entry.baseColor);
            
            // Preserve materialAlpha setting
            const materialAlpha = entry.materialAlpha !== undefined ? entry.materialAlpha : 1.0;
            newMaterial.setFloat('materialAlpha', materialAlpha);
            
            // Enable alpha blending for scrim/transparent materials
            if (materialAlpha < 1.0) {
                newMaterial.backFaceCulling = false;
                newMaterial.alphaMode = BABYLON.Engine.ALPHA_COMBINE;
                newMaterial.needAlphaBlending = function() { return true; };
                newMaterial.needAlphaTesting = function() { return false; };
            }
            
            entry.material = newMaterial;
            entry.mesh.material = newMaterial;
        });
    }
    
    _updateSimpleMode() {
        const numProjectors = Math.min(this.projectors.length, this.maxSimpleProjectors);
        
        this.projectors.forEach(projector => {
            this._getProjectorTexture(projector);
        });
        
        this.receiverMeshes.forEach((entry) => {
            if (!entry.material) return;
            
            entry.material.setInt('numProjectors', numProjectors);
            
            // Set shadow atlas texture
            if (this.shadowAtlasRTT) {
                entry.material.setTexture('shadowAtlas', this.shadowAtlasRTT);
            }
            
            // Set object texture uniforms for reverse projection
            // Get object data from mesh metadata (set by BabylonScene)
            const objData = entry.mesh.metadata?.objectData || {};
            const textureBrightness = (objData.textureBrightness ?? 100) / 100;
            const textureContrast = (objData.textureContrast ?? 100) / 100;
            const textureSaturation = (objData.textureSaturation ?? 100) / 100;
            
            // Check if object has texture - either from metadata OR from entry.objectTexture
            const hasTexture = (objData.textureUrl || entry.objectTexture) ? 1.0 : 0.0;
            
            // Debug logging for reverse projection
            if (this.projectors.some(p => p?.is_reverse_projector)) {
                logger.log(`ReverseProj Debug [${entry.mesh.name}]: hasTexture=${hasTexture}, objData.textureUrl=${!!objData.textureUrl}, entry.objectTexture=${!!entry.objectTexture}`);
            }
            
            entry.material.setFloat('hasObjectTexture', hasTexture);
            entry.material.setFloat('objectTextureBrightness', textureBrightness);
            entry.material.setFloat('objectTextureContrast', textureContrast);
            entry.material.setFloat('objectTextureSaturation', textureSaturation);
            
            // Set object's diffuse texture if available
            if (entry.objectTexture) {
                entry.material.setTexture('objectDiffuseTexture', entry.objectTexture);
            }
            
            for (let i = 0; i < this.maxSimpleProjectors; i++) {
                const projector = this.projectors[i];
                
                if (projector) {
                    const position = new BABYLON.Vector3(
                        projector.position?.x || 0,
                        projector.position?.y || 0,
                        projector.position?.z || 0
                    );
                    
                    // Use the EXACT same VP matrix stored during renderShadowMaps()
                    // This ensures shadow rendering and sampling use identical transforms
                    const vpMatrix = this._vpMatrices[i];
                    // Stacking multiplier — virtual brightness boost (no extra projections)
                    const _s = projector.stacking;
                    const _stkCnt = (_s && _s.enabled)
                        ? 1 + (parseInt(_s.top) || 0) + (parseInt(_s.bottom) || 0) + (parseInt(_s.left) || 0) + (parseInt(_s.right) || 0)
                        : 1;
                    const brightness = ((projector.brightness || 100) / 100) * _stkCnt;
                    
                    entry.material.setVector3(`proj${i}Pos`, position);
                    entry.material.setMatrix(`proj${i}VP`, vpMatrix);
                    entry.material.setFloat(`proj${i}Brightness`, brightness);
                    entry.material.setFloat(`proj${i}Power`, 1.0);
                    
                    // Set reverse projector flag
                    entry.material.setFloat(`proj${i}IsReverse`, projector.is_reverse_projector ? 1.0 : 0.0);
                    
                    // Set border flag (default: enabled)
                    entry.material.setFloat(`proj${i}Border`, projector.show_border !== false ? 1.0 : 0.0);
                    
                    const texture = this.projectorTextures.get(projector.id);
                    if (texture) {
                        entry.material.setTexture(`proj${i}Texture`, texture);
                    }
                    
                } else {
                    // Inactive projector slot
                    entry.material.setFloat(`proj${i}Power`, 0.0);
                    entry.material.setFloat(`proj${i}IsReverse`, 0.0);
                    entry.material.setFloat(`proj${i}Border`, 0.0);
                    entry.material.setTexture(`proj${i}Texture`, this.defaultTextures[i] || this.defaultTextures[0]);
                }
            }
        });
    }
    
    _updateComplexMode() {
        const data = this.projectorDataArray;
        data.fill(0);
        
        this.projectors.forEach((projector, index) => {
            if (index >= MAX_PROJECTORS) return;
            
            const baseOffset = index * 8 * 4;
            
            // Use the EXACT same VP matrix stored during renderShadowMaps()
            // For projectors beyond MAX_SHADOW_PROJECTORS, compute fresh (no shadow tile)
            const vpMatrix = index < MAX_SHADOW_PROJECTORS
                ? this._vpMatrices[index]
                : this._calculateProjectorVPMatrix(projector);
            const m = vpMatrix.m;
            for (let i = 0; i < 16; i++) {
                data[baseOffset + i] = m[i];
            }
            
            data[baseOffset + 16] = projector.position?.x || 0;
            data[baseOffset + 17] = projector.position?.y || 0;
            data[baseOffset + 18] = projector.position?.z || 0;
            data[baseOffset + 19] = (projector.brightness || 100) / 100;
            
            const rotX = (projector.rotation?.x || 0) * Math.PI / 180;
            const rotY = (projector.rotation?.y || 0) * Math.PI / 180;
            const rotZ = (projector.rotation?.z || 0) * Math.PI / 180;
            const rotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
            const dir = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, 1), rotMatrix);
            
            data[baseOffset + 20] = dir.x;
            data[baseOffset + 21] = dir.y;
            data[baseOffset + 22] = dir.z;
            data[baseOffset + 23] = 1.0;
            
            const atlasCol = index % this.atlasColumns;
            const atlasRow = Math.floor(index / this.atlasColumns);
            const uvScale = this.patternSize / this.atlasSize;
            
            data[baseOffset + 24] = atlasCol * uvScale;
            data[baseOffset + 25] = atlasRow * uvScale;
            data[baseOffset + 26] = uvScale;
            data[baseOffset + 27] = uvScale;
            
            // Load custom images for complex mode (same as simple mode)
            // This ensures custom images are cached before _updateAtlasSlot draws them
            const id = projector.id;
            if (projector.projector_image) {
                const cached = this._customImageCache.get(id);
                if (!cached || cached.imageUrl !== projector.projector_image) {
                    logger.log(`ProjectionSystem [Complex]: Loading custom image for projector ${id}`);
                    this._loadAndCacheImage(id, projector.projector_image);
                }
            } else {
                // Image was removed - clear from cache
                if (this._customImageCache.has(id)) {
                    this._customImageCache.delete(id);
                }
            }

            // Video source sync — acquire / release shared HTMLVideoElement.
            // The per-frame `_videoTickObserver` takes it from there.
            const prevVideo = this._customVideoCache.get(id);
            const nextVideoUrl = projector.projector_video || null;
            if ((prevVideo && prevVideo.videoUrl) !== nextVideoUrl) {
                this.loadCustomVideo(id, nextVideoUrl);
            }

            this._updateAtlasSlot(index, projector);
            
            // Write isReverse flag and show_border to pixel 7 (baseOffset + 28)
            const isReverse = projector.is_reverse_projector ? 1.0 : 0.0;
            const showBorder = projector.show_border !== false ? 1.0 : 0.0;
            data[baseOffset + 28] = isReverse;   // .r component
            data[baseOffset + 29] = showBorder;  // .g component
            data[baseOffset + 30] = 0.0;         // .b (unused)
            data[baseOffset + 31] = 0.0;         // .a (unused)
            
            // Debug log first projector data
            if (index === 0) {
                logger.log(`ComplexMode Debug [proj0]: pos=(${data[baseOffset+16].toFixed(2)}, ${data[baseOffset+17].toFixed(2)}, ${data[baseOffset+18].toFixed(2)}), brightness=${data[baseOffset+19].toFixed(2)}, power=${data[baseOffset+23].toFixed(2)}, isReverse=${isReverse}`);
            }
        });
        
        this._uploadAtlasToRawTexture();
        this.projectorDataTexture.update(data);
        
        logger.log(`ComplexMode: Updated ${this.projectors.length} projectors, dataTexWidth=${DATA_TEX_WIDTH}, atlasSize=${this.atlasSize}`);
        
        this.receiverMeshes.forEach((entry) => {
            if (entry.material) {
                entry.material.setInt('numProjectors', this.projectors.length);
                
                // Set shadow atlas texture
                if (this.shadowAtlasRTT) {
                    entry.material.setTexture('shadowAtlas', this.shadowAtlasRTT);
                }
                
                // Set object texture uniforms for reverse projection (complex mode)
                const objData = entry.mesh.metadata?.objectData || {};
                const textureBrightness = (objData.textureBrightness ?? 100) / 100;
                const textureContrast = (objData.textureContrast ?? 100) / 100;
                const textureSaturation = (objData.textureSaturation ?? 100) / 100;
                const hasTexture = (objData.textureUrl || entry.objectTexture) ? 1.0 : 0.0;
                
                entry.material.setFloat('hasObjectTexture', hasTexture);
                entry.material.setFloat('objectTextureBrightness', textureBrightness);
                entry.material.setFloat('objectTextureContrast', textureContrast);
                entry.material.setFloat('objectTextureSaturation', textureSaturation);
                
                if (entry.objectTexture) {
                    entry.material.setTexture('objectDiffuseTexture', entry.objectTexture);
                }
            }
        });
    }
    

    setOverlapBrightness(value) {
        const v = Math.max(0, Math.min(1, value));
        // Sticky: future register* calls will use this value too. Without
        // this, materials registered AFTER setOverlapBrightness() (typical
        // on project reload — model loads async, settings sync first) would
        // silently fall back to the 1.0 default and ignore the saved value.
        this._overlapBrightness = v;
        for (const [, entry] of this.receiverMeshes) {
            if (entry.material) {
                try { entry.material.setFloat('overlapBrightness', v); } catch {}
            }
        }
    }

    dispose() {
        this.receiverMeshes.forEach((entry) => {
            if (entry.mesh && entry.originalMaterial) {
                entry.mesh.material = entry.originalMaterial;
            }
            if (entry.material) {
                entry.material.dispose();
            }
        });
        this.receiverMeshes.clear();
        this.occluderMeshes.clear();
        
        this.projectorTextures.forEach(t => t.dispose());
        this.projectorTextures.clear();
        
        this.defaultTextures.forEach(t => t.dispose());
        this.defaultTextures = [];
        
        if (this.projectorDataTexture) {
            this.projectorDataTexture.dispose();
        }
        
        if (this.projectionAtlas) {
            this.projectionAtlas.dispose();
        }
        if (this.atlasRawTexture) {
            this.atlasRawTexture.dispose();
        }
        
        // Dispose shadow atlas system
        if (this.shadowAtlasRTT) {
            const idx = this.scene.customRenderTargets.indexOf(this.shadowAtlasRTT);
            if (idx >= 0) this.scene.customRenderTargets.splice(idx, 1);
            this.shadowAtlasRTT.dispose();
            this.shadowAtlasRTT = null;
        }
        
        if (this.depthMaterial) {
            this.depthMaterial.dispose();
            this.depthMaterial = null;
        }
        
        // Dispose per-projector depth material pool
        if (this.depthMaterials) {
            this.depthMaterials.forEach(mat => mat.dispose());
            this.depthMaterials = [];
        }

        // Release all video sources held by this projection system. The
        // shared HTMLVideoElements are refcounted — if no other consumer
        // holds the URL, the manager pauses + removes the element.
        if (this._customVideoCache) {
            for (const [projectorId, entry] of this._customVideoCache) {
                if (entry && entry.videoUrl) {
                    try { releaseVideo(entry.videoUrl, projectorId); } catch { /* ignore */ }
                }
            }
            this._customVideoCache.clear();
        }
        if (this._videoTickObserver && this.scene && this.scene.onBeforeRenderObservable) {
            this.scene.onBeforeRenderObservable.remove(this._videoTickObserver);
            this._videoTickObserver = null;
        }

        logger.log('ProjectionSystem: Disposed');
    }
}

export default ProjectionSystem;
